import React, { useState, useEffect, useCallback } from 'react';

// ============================================================
// DROP 50 — FINANCIAL HUB — Master Container
// Tabs: Dashboard | Chart of Accounts | Journals | Trial Balance
//       Budget | Workflow Tasks | Minutes
// ============================================================

const C = {
  bg: '#070D0A', surface: '#0E1812', card: '#121E16', border: '#1C2E22',
  text: '#EDE8DC', muted: 'rgba(237,232,220,0.5)', green: '#0F5132',
  gold: '#C9A84C', goldLt: '#E2C97E', red: '#ef4444', blue: '#60a5fa',
  teal: '#2dd4bf', purple: '#a78bfa', orange: '#fb923c',
};

const fmt = (n: number, currency = 'NGN') => {
  const symbol = currency === 'NGN' ? '₦' : currency === 'USD' ? '$' : currency === 'GBP' ? '£' : '€';
  return `${symbol}${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

interface Props {
  currentUser?: { email?: string; systemRole?: string; role?: string; name?: string } | null;
}

type HubTab = 'dashboard' | 'accounts' | 'journals' | 'trial-balance' | 'ie-summary' | 'budget' | 'tasks' | 'minutes' | 'banking' | 'payroll' | 'tax' | 'inventory' | 'reports';

const TABS: { id: HubTab; label: string; emoji: string; desc: string }[] = [
  { id: 'dashboard',     label: 'Dashboard',          emoji: '📊', desc: 'Financial overview' },
  { id: 'accounts',      label: 'Chart of Accounts',  emoji: '📚', desc: 'Account codes & classes' },
  { id: 'journals',      label: 'Journal Entries',    emoji: '📝', desc: 'Double-entry bookkeeping' },
  { id: 'trial-balance', label: 'Trial Balance',      emoji: '⚖️', desc: 'Account balances' },
  { id: 'ie-summary',    label: 'Income & Expenditure',emoji: '💰', desc: 'Revenue vs Expenses' },
  { id: 'budget',        label: 'Budget Management',  emoji: '🎯', desc: 'Full budget lifecycle' },
  { id: 'tasks',         label: 'Workflow Tasks',     emoji: '✅', desc: 'Task management' },
  { id: 'minutes',       label: 'Meeting Minutes',    emoji: '📋', desc: 'Committee minutes' },
  { id: 'banking',       label: 'Banking',            emoji: '🏦', desc: 'Bank accounts + cash flow' },
  { id: 'payroll',       label: 'Payroll & Staff',    emoji: '👥', desc: 'Staff, allowances, advances' },
  { id: 'tax',           label: 'Tax Records',        emoji: '⚖️', desc: 'WHT, PAYE — CAMA 2020' },
  { id: 'inventory',     label: 'Inventory & Assets', emoji: '📦', desc: 'Stock + fixed assets' },
  { id: 'reports',       label: 'Reports',            emoji: '📊', desc: 'Full financial & operational reports' },
];

const FinancialHub: React.FC<Props> = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState<HubTab>('dashboard');
  const [dashboard, setDashboard] = useState<any>(null);
  const [loading, setLoading]     = useState(false);
  const [year]                    = useState(2026);

  const token = localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/finance/dashboard?year=${year}`, { headers });
      const d = await r.json();
      setDashboard(d);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 6,
    border: `1px solid ${C.border}`, background: C.surface,
    color: C.text, fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const btnPrimary: React.CSSProperties = { padding: '9px 20px', borderRadius: 8, border: 'none', background: C.gold, color: '#000', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
  const btnGhost: React.CSSProperties  = { padding: '9px 20px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'none', color: C.text, fontSize: 13, cursor: 'pointer' };
  const btnDanger: React.CSSProperties = { padding: '8px 16px', borderRadius: 8, border: 'none', background: C.red, color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' };
  const labelStyle: React.CSSProperties = { fontSize: 11, color: C.muted, marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' };

  // ── DASHBOARD ─────────────────────────────────────────────────
  const renderDashboard = () => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, fontSize: 20, margin: 0 }}>Financial Dashboard — {year}</h2>
          <p style={{ color: C.muted, fontSize: 13, margin: '4px 0 0' }}>ERUE Festival of Uzairue Kingdom — Real-time financial overview</p>
        </div>
        <button onClick={fetchDashboard} style={btnGhost}>🔄 Refresh</button>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Total Income', value: fmt(dashboard?.totalIncome || 0), color: '#4ade80', emoji: '💚', sub: 'Posted journals' },
          { label: 'Total Expenses', value: fmt(dashboard?.totalExpense || 0), color: C.red, emoji: '💸', sub: 'Posted journals' },
          { label: 'Surplus / Deficit', value: fmt((dashboard?.totalIncome || 0) - (dashboard?.totalExpense || 0)), color: (dashboard?.surplus || 0) >= 0 ? '#4ade80' : C.red, emoji: '📊', sub: 'Net position' },
          { label: 'Budget Utilisation', value: `${(dashboard?.budgetUtilisation || 0).toFixed(1)}%`, color: C.gold, emoji: '🎯', sub: 'vs Approved budget' },
          { label: 'Journal Entries', value: dashboard?.journals || 0, color: C.blue, emoji: '📝', sub: `Year ${year}` },
          { label: 'Account Codes', value: dashboard?.accounts || 0, color: C.teal, emoji: '📚', sub: 'Chart of Accounts' },
          { label: 'Pending Tasks', value: dashboard?.pendingTasks || 0, color: C.orange, emoji: '⏳', sub: 'Workflow tasks' },
          { label: 'Meeting Minutes', value: dashboard?.minutes || 0, color: C.purple, emoji: '📋', sub: `Year ${year}` },
        ].map(k => (
          <div key={k.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: 24 }}>{k.emoji}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: k.color, marginTop: 4 }}>{k.value}</div>
            <div style={{ fontSize: 12, color: C.text, marginTop: 2 }}>{k.label}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <h3 style={{ color: C.goldLt, fontSize: 15, margin: '0 0 16px', fontFamily: "'Cinzel', serif" }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { label: '📚 Manage Accounts', tab: 'accounts' as HubTab },
            { label: '📝 New Journal Entry', tab: 'journals' as HubTab },
            { label: '⚖️ View Trial Balance', tab: 'trial-balance' as HubTab },
            { label: '🎯 Manage Budget', tab: 'budget' as HubTab },
            { label: '✅ View Tasks', tab: 'tasks' as HubTab },
            { label: '📋 Meeting Minutes', tab: 'minutes' as HubTab },
          ].map(a => (
            <button key={a.tab} onClick={() => setActiveTab(a.tab)} style={{ ...btnGhost, fontSize: 12 }}>{a.label}</button>
          ))}
        </div>
      </div>

      {/* CAMA 2020 Note */}
      <div style={{ background: 'rgba(201,168,76,0.08)', border: `1px solid ${C.gold}44`, borderRadius: 10, padding: '14px 18px', fontSize: 13 }}>
        <span style={{ color: C.gold, fontWeight: 700 }}>⚖️ CAMA 2020 Non-Profit Compliance Note: </span>
        <span style={{ color: C.muted }}>ERUE Festival of Uzairue Kingdom operates as a cultural non-profit entity. All financial records are maintained on accrual basis. WHT obligations apply to qualifying vendor payments. Annual returns due to CAC by June 30.</span>
      </div>
    </div>
  );

  // ── CHART OF ACCOUNTS ────────────────────────────────────────
  const ChartOfAccountsPanel = () => {
    const [accounts, setAccounts]   = useState<any[]>([]);
    const [loading, setLoading]     = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem]   = useState<any>(null);
    const [seeding, setSeeding]     = useState(false);
    const [filter, setFilter]       = useState('');
    const [filterClass, setFilterClass] = useState('');
    const [form, setForm] = useState({ code: '', name: '', class: 'ASSET', subClass: '', description: '', currency: 'NGN' });
    const [flash, setFlash] = useState('');
    const [flashType, setFlashType] = useState<'ok'|'err'>('ok');

    const showFlash = (msg: string, type: 'ok'|'err' = 'ok') => {
      setFlash(msg); setFlashType(type);
      setTimeout(() => setFlash(''), 4000);
    };

    const fetchAccounts = useCallback(async () => {
      setLoading(true);
      try {
        const r = await fetch('/api/finance/accounts', { headers });
        const d = await r.json();
        setAccounts(d.accounts || []);
      } catch { showFlash('Failed to load accounts', 'err'); }
      finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

    const handleSeed = async () => {
      setSeeding(true);
      try {
        const r = await fetch('/api/finance/accounts/seed', { method: 'POST', headers });
        const d = await r.json();
        showFlash(d.message || `✅ Seeded ${d.seeded} accounts`);
        fetchAccounts();
      } catch { showFlash('Seed failed', 'err'); }
      finally { setSeeding(false); }
    };

    const handleSave = async () => {
      try {
        const url    = editItem ? `/api/finance/accounts/${editItem.id}` : '/api/finance/accounts';
        const method = editItem ? 'PUT' : 'POST';
        const r = await fetch(url, { method, headers, body: JSON.stringify(form) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash(editItem ? 'Account updated' : 'Account created');
        setShowModal(false);
        fetchAccounts();
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const handleDelete = async (id: string) => {
      if (!confirm('Delete this account code?')) return;
      try {
        const r = await fetch(`/api/finance/accounts/${id}`, { method: 'DELETE', headers });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash('Deleted'); fetchAccounts();
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const CLASSES = ['ASSET','LIABILITY','NET_ASSET','INCOME','EXPENSE','PAYROLL'];
    const CLASS_COLORS: Record<string, string> = {
      ASSET: '#4ade80', LIABILITY: C.red, NET_ASSET: C.purple,
      INCOME: C.teal, EXPENSE: C.orange, PAYROLL: C.blue,
    };

    const filtered = accounts.filter(a => {
      if (filterClass && a.class !== filterClass) return false;
      if (filter) {
        const q = filter.toLowerCase();
        return a.code.toLowerCase().includes(q) || a.name.toLowerCase().includes(q);
      }
      return true;
    });

    const groupedByClass = CLASSES.reduce((acc, cls) => {
      acc[cls] = filtered.filter(a => a.class === cls);
      return acc;
    }, {} as Record<string, any[]>);

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
          <h2 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, fontSize: 18, margin: 0 }}>📚 Chart of Accounts</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            {accounts.length === 0 && <button onClick={handleSeed} disabled={seeding} style={{ ...btnGhost, fontSize: 12 }}>{seeding ? 'Seeding...' : '🌱 Seed Default COA'}</button>}
            <button onClick={() => { setEditItem(null); setForm({ code:'',name:'',class:'ASSET',subClass:'',description:'',currency:'NGN' }); setShowModal(true); }} style={btnPrimary}>+ Add Account</button>
          </div>
        </div>

        {flash && <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 12, fontSize: 13, background: flashType === 'ok' ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${flashType === 'ok' ? '#4ade80' : C.red}`, color: flashType === 'ok' ? '#4ade80' : C.red }}>{flash}</div>}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search code or name..." style={{ ...inputStyle, maxWidth: 260 }} />
          <select value={filterClass} onChange={e => setFilterClass(e.target.value)} style={{ ...inputStyle, maxWidth: 180 }}>
            <option value="">All Classes</option>
            {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <span style={{ color: C.muted, fontSize: 12, alignSelf: 'center' }}>{filtered.length} accounts</span>
        </div>

        {/* Accounts by class */}
        {loading ? <div style={{ textAlign: 'center', padding: 40, color: C.muted }}>Loading...</div> : (
          CLASSES.map(cls => {
            const items = groupedByClass[cls];
            if (!items || items.length === 0) return null;
            return (
              <div key={cls} style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: CLASS_COLORS[cls] }} />
                  <span style={{ color: CLASS_COLORS[cls], fontWeight: 700, fontSize: 13 }}>{cls}</span>
                  <span style={{ color: C.muted, fontSize: 11 }}>({items.length} accounts)</span>
                </div>
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: C.surface }}>
                        {['Code','Account Name','Description','Active','Actions'].map(h => (
                          <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: C.muted, fontSize: 11, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((a, i) => (
                        <tr key={a.id} style={{ borderBottom: i < items.length-1 ? `1px solid ${C.border}` : 'none' }}>
                          <td style={{ padding: '8px 12px', color: CLASS_COLORS[cls], fontWeight: 700, fontFamily: 'monospace' }}>{a.code}</td>
                          <td style={{ padding: '8px 12px', color: C.text }}>{a.name} {a.isSystem && <span style={{ fontSize: 10, color: C.muted, background: C.surface, borderRadius: 4, padding: '1px 5px', marginLeft: 4 }}>SYSTEM</span>}</td>
                          <td style={{ padding: '8px 12px', color: C.muted, fontSize: 12 }}>{a.description || '—'}</td>
                          <td style={{ padding: '8px 12px', color: a.isActive ? '#4ade80' : C.red }}>{a.isActive ? '✅' : '❌'}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => { setEditItem(a); setForm({ code: a.code, name: a.name, class: a.class, subClass: a.subClass||'', description: a.description||'', currency: a.currency||'NGN' }); setShowModal(true); }} style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }}>✏️</button>
                              {!a.isSystem && <button onClick={() => handleDelete(a.id)} style={{ ...btnDanger, padding: '4px 10px' }}>🗑</button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 500 }}>
              <h3 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, margin: '0 0 20px' }}>{editItem ? 'Edit Account' : 'Add Account Code'}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label style={labelStyle}>Account Code *</label>
                  <input value={form.code} onChange={e => setForm(f => ({...f, code: e.target.value}))} style={inputStyle} placeholder="e.g. 5100" disabled={!!editItem} />
                </div>
                <div>
                  <label style={labelStyle}>Class *</label>
                  <select value={form.class} onChange={e => setForm(f => ({...f, class: e.target.value}))} style={inputStyle}>
                    {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Account Name *</label>
                  <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} style={inputStyle} placeholder="Full account name" />
                </div>
                <div>
                  <label style={labelStyle}>Sub-Class</label>
                  <input value={form.subClass} onChange={e => setForm(f => ({...f, subClass: e.target.value}))} style={inputStyle} placeholder="Optional" />
                </div>
                <div>
                  <label style={labelStyle}>Currency</label>
                  <select value={form.currency} onChange={e => setForm(f => ({...f, currency: e.target.value}))} style={inputStyle}>
                    {['NGN','USD','GBP','EUR'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Description</label>
                  <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} style={inputStyle} placeholder="Optional description" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button onClick={() => setShowModal(false)} style={btnGhost}>Cancel</button>
                <button onClick={handleSave} style={btnPrimary}>{editItem ? 'Update' : 'Add Account'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── JOURNAL ENTRIES ──────────────────────────────────────────
  const JournalLedgerPanel = () => {
    const [journals, setJournals]   = useState<any[]>([]);
    const [accounts, setAccounts]   = useState<any[]>([]);
    const [loading, setLoading]     = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [viewJournal, setViewJournal] = useState<any>(null);
    const [flash, setFlash]         = useState('');
    const [flashType, setFlashType] = useState<'ok'|'err'>('ok');
    const [filterStatus, setFilterStatus] = useState('');
    const [form, setForm] = useState({
      date: new Date().toISOString().split('T')[0],
      description: '', entryType: 'GENERAL', currency: 'NGN', exchangeRate: '1', notes: '',
      lines: [
        { accountCodeId: '', description: '', debit: '', credit: '', currency: 'NGN' },
        { accountCodeId: '', description: '', debit: '', credit: '', currency: 'NGN' },
      ]
    });

    const showFlash = (msg: string, type: 'ok'|'err' = 'ok') => {
      setFlash(msg); setFlashType(type); setTimeout(() => setFlash(''), 4000);
    };

    const fetchAll = useCallback(async () => {
      setLoading(true);
      try {
        const [jr, ar] = await Promise.all([
          fetch(`/api/finance/journals?year=${year}${filterStatus ? `&status=${filterStatus}` : ''}`, { headers }),
          fetch('/api/finance/accounts', { headers }),
        ]);
        const jd = await jr.json(); const ad = await ar.json();
        setJournals(jd.journals || []); setAccounts(ad.accounts || []);
      } catch { showFlash('Failed to load', 'err'); }
      finally { setLoading(false); }
    }, [filterStatus]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { accountCodeId: '', description: '', debit: '', credit: '', currency: 'NGN' }] }));
    const removeLine = (i: number) => setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
    const updateLine = (i: number, field: string, val: string) => setForm(f => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [field]: val } : l) }));

    const totalDebit  = form.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
    const totalCredit = form.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    const isBalanced  = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

    const handleSave = async (post = false) => {
      if (!isBalanced) { showFlash('Journal must balance (Debit = Credit)', 'err'); return; }
      try {
        const r = await fetch('/api/finance/journals', { method: 'POST', headers, body: JSON.stringify({ ...form, festivalYear: year }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        if (post) {
          const pr = await fetch(`/api/finance/journals/${d.id}/post`, { method: 'PUT', headers });
          if (!pr.ok) { const pd = await pr.json(); throw new Error(pd.error); }
        }
        showFlash(post ? 'Journal saved and posted' : 'Journal saved as draft');
        setShowModal(false);
        fetchAll();
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const handleAction = async (id: string, action: 'post'|'void') => {
      if (action === 'void' && !confirm('Void this journal entry? This cannot be undone.')) return;
      try {
        const r = await fetch(`/api/finance/journals/${id}/${action}`, { method: 'PUT', headers });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash(`Journal ${action}ed`); fetchAll(); setViewJournal(null);
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const handleDelete = async (id: string) => {
      if (!confirm('Delete this draft journal?')) return;
      try {
        const r = await fetch(`/api/finance/journals/${id}`, { method: 'DELETE', headers });
        if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
        showFlash('Deleted'); fetchAll();
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const STATUS_COLORS: Record<string, string> = { DRAFT: C.muted, POSTED: '#4ade80', VOID: C.red };
    const ENTRY_TYPES = ['GENERAL','RECEIPT','PAYMENT','PAYROLL','BANK','ADJUSTMENT'];
    const acctName = (id: string) => { const a = accounts.find(a => a.id === id); return a ? `${a.code} — ${a.name}` : id; };

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
          <h2 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, fontSize: 18, margin: 0 }}>📝 Journal Entries</h2>
          <button onClick={() => setShowModal(true)} style={btnPrimary}>+ New Entry</button>
        </div>

        {flash && <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 12, fontSize: 13, background: flashType === 'ok' ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${flashType === 'ok' ? '#4ade80' : C.red}`, color: flashType === 'ok' ? '#4ade80' : C.red }}>{flash}</div>}

        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, maxWidth: 180 }}>
            <option value="">All Status</option>
            <option value="DRAFT">Draft</option>
            <option value="POSTED">Posted</option>
            <option value="VOID">Void</option>
          </select>
          <button onClick={fetchAll} style={btnGhost}>🔄</button>
        </div>

        {loading ? <div style={{ textAlign: 'center', padding: 40, color: C.muted }}>Loading...</div> :
          journals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>
              <div style={{ fontSize: 40 }}>📝</div>
              <div>No journal entries yet</div>
              <button onClick={() => setShowModal(true)} style={{ ...btnPrimary, marginTop: 16 }}>Create First Entry</button>
            </div>
          ) : (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.surface }}>
                    {['Reference','Date','Description','Type','Dr Total','Cr Total','Status','Actions'].map(h => (
                      <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: C.muted, fontSize: 11, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {journals.map((j, i) => {
                    const dr = j.lines?.reduce((s: number, l: any) => s + l.debit, 0) || 0;
                    const cr = j.lines?.reduce((s: number, l: any) => s + l.credit, 0) || 0;
                    return (
                      <tr key={j.id} style={{ borderBottom: i < journals.length-1 ? `1px solid ${C.border}` : 'none' }}>
                        <td style={{ padding: '8px 12px', color: C.blue, fontFamily: 'monospace', fontSize: 12 }}>{j.reference}</td>
                        <td style={{ padding: '8px 12px', color: C.muted, fontSize: 12 }}>{new Date(j.date).toLocaleDateString()}</td>
                        <td style={{ padding: '8px 12px', color: C.text, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.description}</td>
                        <td style={{ padding: '8px 12px', color: C.muted, fontSize: 11 }}>{j.entryType}</td>
                        <td style={{ padding: '8px 12px', color: '#4ade80', textAlign: 'right' }}>₦{dr.toLocaleString()}</td>
                        <td style={{ padding: '8px 12px', color: C.orange, textAlign: 'right' }}>₦{cr.toLocaleString()}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{ background: STATUS_COLORS[j.status] + '22', border: `1px solid ${STATUS_COLORS[j.status]}44`, color: STATUS_COLORS[j.status], borderRadius: 12, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>{j.status}</span>
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => setViewJournal(j)} style={{ ...btnGhost, padding: '4px 8px', fontSize: 11 }}>👁</button>
                            {j.status === 'DRAFT' && <>
                              <button onClick={() => handleAction(j.id, 'post')} style={{ ...btnPrimary, padding: '4px 8px', fontSize: 11 }}>✓ Post</button>
                              <button onClick={() => handleDelete(j.id)} style={{ ...btnDanger, padding: '4px 8px' }}>🗑</button>
                            </>}
                            {j.status === 'POSTED' && <button onClick={() => handleAction(j.id, 'void')} style={{ ...btnDanger, padding: '4px 8px', fontSize: 11 }}>Void</button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        }

        {/* New Entry Modal */}
        {showModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 800, maxHeight: '95vh', overflowY: 'auto' }}>
              <h3 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, margin: '0 0 20px' }}>📝 New Journal Entry</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Date *</label>
                  <input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Entry Type</label>
                  <select value={form.entryType} onChange={e => setForm(f => ({...f, entryType: e.target.value}))} style={inputStyle}>
                    {ENTRY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Currency</label>
                  <select value={form.currency} onChange={e => setForm(f => ({...f, currency: e.target.value}))} style={inputStyle}>
                    {['NGN','USD','GBP','EUR'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Description *</label>
                  <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} style={inputStyle} placeholder="Journal description" />
                </div>
                {form.currency !== 'NGN' && (
                  <div>
                    <label style={labelStyle}>Exchange Rate to NGN</label>
                    <input type="number" value={form.exchangeRate} onChange={e => setForm(f => ({...f, exchangeRate: e.target.value}))} style={inputStyle} placeholder="e.g. 1650" />
                  </div>
                )}
              </div>

              {/* Journal Lines */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Journal Lines</label>
                  <button onClick={addLine} style={{ ...btnGhost, padding: '4px 12px', fontSize: 12 }}>+ Add Line</button>
                </div>
                <div style={{ background: C.surface, borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}` }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr>
                        {['Account','Description','Debit (₦)','Credit (₦)',''].map(h => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: C.muted, fontSize: 11, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {form.lines.map((line, i) => (
                        <tr key={i}>
                          <td style={{ padding: '6px 8px' }}>
                            <select value={line.accountCodeId} onChange={e => updateLine(i, 'accountCodeId', e.target.value)} style={{ ...inputStyle, padding: '5px 8px', fontSize: 12 }}>
                              <option value="">-- Select account --</option>
                              {accounts.filter(a => a.isActive).map(a => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                            </select>
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <input value={line.description} onChange={e => updateLine(i, 'description', e.target.value)} style={{ ...inputStyle, padding: '5px 8px', fontSize: 12 }} placeholder="Narration" />
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <input type="number" value={line.debit} onChange={e => updateLine(i, 'debit', e.target.value)} style={{ ...inputStyle, padding: '5px 8px', fontSize: 12, textAlign: 'right' }} placeholder="0.00" />
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <input type="number" value={line.credit} onChange={e => updateLine(i, 'credit', e.target.value)} style={{ ...inputStyle, padding: '5px 8px', fontSize: 12, textAlign: 'right' }} placeholder="0.00" />
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            {form.lines.length > 2 && <button onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 16 }}>×</button>}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ background: C.card, borderTop: `2px solid ${C.border}` }}>
                        <td colSpan={2} style={{ padding: '8px 10px', color: C.muted, fontSize: 12, fontWeight: 700 }}>TOTALS</td>
                        <td style={{ padding: '8px 10px', color: '#4ade80', fontWeight: 700, textAlign: 'right' }}>₦{totalDebit.toLocaleString()}</td>
                        <td style={{ padding: '8px 10px', color: C.orange, fontWeight: 700, textAlign: 'right' }}>₦{totalCredit.toLocaleString()}</td>
                        <td style={{ padding: '8px 10px' }}>
                          {isBalanced ? <span style={{ color: '#4ade80', fontSize: 16 }}>✓</span> : <span style={{ color: C.red, fontSize: 16 }}>✗</span>}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {!isBalanced && totalDebit > 0 && (
                  <div style={{ color: C.red, fontSize: 12, marginTop: 6 }}>
                    ⚠️ Out of balance by ₦{Math.abs(totalDebit - totalCredit).toLocaleString()}
                  </div>
                )}
              </div>

              <div>
                <label style={labelStyle}>Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Optional notes" />
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button onClick={() => setShowModal(false)} style={btnGhost}>Cancel</button>
                <button onClick={() => handleSave(false)} disabled={!isBalanced} style={{ ...btnGhost, opacity: isBalanced ? 1 : 0.5 }}>Save as Draft</button>
                <button onClick={() => handleSave(true)} disabled={!isBalanced} style={{ ...btnPrimary, opacity: isBalanced ? 1 : 0.5 }}>Save & Post</button>
              </div>
            </div>
          </div>
        )}

        {/* View Journal Modal */}
        {viewJournal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                  <h3 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, margin: 0 }}>{viewJournal.reference}</h3>
                  <p style={{ color: C.muted, fontSize: 13, margin: '4px 0 0' }}>{viewJournal.description}</p>
                </div>
                <span style={{ background: STATUS_COLORS[viewJournal.status] + '22', border: `1px solid ${STATUS_COLORS[viewJournal.status]}44`, color: STATUS_COLORS[viewJournal.status], borderRadius: 12, padding: '4px 14px', fontSize: 12, fontWeight: 700 }}>{viewJournal.status}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13, marginBottom: 20 }}>
                {[
                  ['Date', new Date(viewJournal.date).toLocaleDateString()],
                  ['Type', viewJournal.entryType],
                  ['Currency', viewJournal.currency],
                  ['Created by', viewJournal.createdBy || '—'],
                  ['Posted by', viewJournal.postedBy || '—'],
                  ['Posted at', viewJournal.postedAt ? new Date(viewJournal.postedAt).toLocaleString() : '—'],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: C.muted, minWidth: 80 }}>{k}</span>
                    <span style={{ color: C.text }}>{v}</span>
                  </div>
                ))}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 20 }}>
                <thead>
                  <tr style={{ background: C.surface }}>
                    {['Account','Description','Debit','Credit'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Debit' || h === 'Credit' ? 'right' : 'left', color: C.muted, fontSize: 11, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {viewJournal.lines?.map((l: any, i: number) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '8px 12px', color: C.blue }}>{acctName(l.accountCodeId)}</td>
                      <td style={{ padding: '8px 12px', color: C.muted }}>{l.description || '—'}</td>
                      <td style={{ padding: '8px 12px', color: '#4ade80', textAlign: 'right' }}>{l.debit > 0 ? `₦${l.debit.toLocaleString()}` : '—'}</td>
                      <td style={{ padding: '8px 12px', color: C.orange, textAlign: 'right' }}>{l.credit > 0 ? `₦${l.credit.toLocaleString()}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                {viewJournal.status === 'DRAFT' && <button onClick={() => handleAction(viewJournal.id, 'post')} style={btnPrimary}>✓ Post Journal</button>}
                {viewJournal.status === 'POSTED' && <button onClick={() => handleAction(viewJournal.id, 'void')} style={btnDanger}>Void</button>}
                <button onClick={() => setViewJournal(null)} style={btnGhost}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── TRIAL BALANCE ────────────────────────────────────────────
  const TrialBalancePanel = () => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [ieData, setIeData]   = useState<any>(null);
    const [view, setView]       = useState<'tb'|'ie'>('tb');

    const fetchAll = useCallback(async () => {
      setLoading(true);
      try {
        const [tbr, ier] = await Promise.all([
          fetch(`/api/finance/trial-balance?year=${year}`, { headers }),
          fetch(`/api/finance/income-expenditure?year=${year}`, { headers }),
        ]);
        const tbd = await tbr.json(); const ied = await ier.json();
        setData(tbd); setIeData(ied);
      } catch { /* silent */ }
      finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const CLASS_ORDER = ['ASSET','LIABILITY','NET_ASSET','INCOME','EXPENSE','PAYROLL'];
    const CLASS_COLORS: Record<string, string> = {
      ASSET:'#4ade80', LIABILITY:C.red, NET_ASSET:C.purple,
      INCOME:C.teal, EXPENSE:C.orange, PAYROLL:C.blue
    };

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
          <h2 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, fontSize: 18, margin: 0 }}>⚖️ Financial Statements — {year}</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setView('tb')} style={{ ...btnGhost, background: view === 'tb' ? C.gold : 'none', color: view === 'tb' ? '#000' : C.text }}>Trial Balance</button>
            <button onClick={() => setView('ie')} style={{ ...btnGhost, background: view === 'ie' ? C.gold : 'none', color: view === 'ie' ? '#000' : C.text }}>Income & Expenditure</button>
            <button onClick={fetchAll} style={btnGhost}>🔄</button>
          </div>
        </div>

        {loading ? <div style={{ textAlign: 'center', padding: 40, color: C.muted }}>Calculating...</div> : (
          view === 'tb' ? (
            data && (
              <>
                {/* Summary */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 20 }}>
                  {[
                    { label: 'Total Debits', value: fmt(data.totals?.totalDebit || 0), color: '#4ade80' },
                    { label: 'Total Credits', value: fmt(data.totals?.totalCredit || 0), color: C.orange },
                    { label: 'Balance', value: Math.abs((data.totals?.totalDebit || 0) - (data.totals?.totalCredit || 0)) < 0.01 ? '✓ BALANCED' : '⚠ OUT OF BALANCE', color: Math.abs((data.totals?.totalDebit || 0) - (data.totals?.totalCredit || 0)) < 0.01 ? '#4ade80' : C.red },
                  ].map(s => (
                    <div key={s.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {data.trialBalance?.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>No posted journal entries for {year}</div>
                ) : (
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: C.surface }}>
                          {['Code','Account Name','Class','Debit','Credit','Balance'].map(h => (
                            <th key={h} style={{ padding: '9px 12px', textAlign: ['Debit','Credit','Balance'].includes(h) ? 'right' : 'left', color: C.muted, fontSize: 11, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {CLASS_ORDER.map(cls => {
                          const items = (data.trialBalance || []).filter((a: any) => a.class === cls);
                          if (items.length === 0) return null;
                          return [
                            <tr key={`hdr-${cls}`} style={{ background: CLASS_COLORS[cls] + '11' }}>
                              <td colSpan={6} style={{ padding: '6px 12px', color: CLASS_COLORS[cls], fontWeight: 700, fontSize: 11 }}>{cls}</td>
                            </tr>,
                            ...items.map((a: any, i: number) => (
                              <tr key={a.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                                <td style={{ padding: '8px 12px', color: CLASS_COLORS[a.class], fontFamily: 'monospace' }}>{a.code}</td>
                                <td style={{ padding: '8px 12px', color: C.text }}>{a.name}</td>
                                <td style={{ padding: '8px 12px', color: C.muted, fontSize: 11 }}>{a.class}</td>
                                <td style={{ padding: '8px 12px', color: '#4ade80', textAlign: 'right' }}>₦{a.debit.toLocaleString()}</td>
                                <td style={{ padding: '8px 12px', color: C.orange, textAlign: 'right' }}>₦{a.credit.toLocaleString()}</td>
                                <td style={{ padding: '8px 12px', color: a.balance >= 0 ? '#4ade80' : C.red, textAlign: 'right', fontWeight: 600 }}>₦{Math.abs(a.balance).toLocaleString()}{a.balance < 0 ? ' (Cr)' : ''}</td>
                              </tr>
                            ))
                          ];
                        })}
                        <tr style={{ background: C.surface, borderTop: `2px solid ${C.border}` }}>
                          <td colSpan={3} style={{ padding: '10px 12px', color: C.gold, fontWeight: 700 }}>GRAND TOTALS</td>
                          <td style={{ padding: '10px 12px', color: '#4ade80', textAlign: 'right', fontWeight: 700 }}>₦{(data.totals?.totalDebit || 0).toLocaleString()}</td>
                          <td style={{ padding: '10px 12px', color: C.orange, textAlign: 'right', fontWeight: 700 }}>₦{(data.totals?.totalCredit || 0).toLocaleString()}</td>
                          <td style={{ padding: '10px 12px' }} />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )
          ) : (
            ieData && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                {/* Income */}
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '12px 16px', background: 'rgba(45,212,191,0.1)', borderBottom: `1px solid ${C.border}` }}>
                    <h3 style={{ color: C.teal, margin: 0, fontSize: 14 }}>📥 INCOME</h3>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <tbody>
                      {Object.entries(ieData.income || {}).map(([name, amt]: [string, any]) => (
                        <tr key={name} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: '8px 12px', color: C.text }}>{name}</td>
                          <td style={{ padding: '8px 12px', color: C.teal, textAlign: 'right', fontWeight: 600 }}>{fmt(amt)}</td>
                        </tr>
                      ))}
                      <tr style={{ background: C.surface, borderTop: `2px solid ${C.border}` }}>
                        <td style={{ padding: '10px 12px', color: C.gold, fontWeight: 700 }}>Total Income</td>
                        <td style={{ padding: '10px 12px', color: C.teal, textAlign: 'right', fontWeight: 700, fontSize: 15 }}>{fmt(ieData.totalIncome || 0)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {/* Expenditure */}
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '12px 16px', background: 'rgba(251,146,60,0.1)', borderBottom: `1px solid ${C.border}` }}>
                    <h3 style={{ color: C.orange, margin: 0, fontSize: 14 }}>📤 EXPENDITURE</h3>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <tbody>
                      {Object.entries(ieData.expense || {}).map(([name, amt]: [string, any]) => (
                        <tr key={name} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: '8px 12px', color: C.text }}>{name}</td>
                          <td style={{ padding: '8px 12px', color: C.orange, textAlign: 'right', fontWeight: 600 }}>{fmt(amt)}</td>
                        </tr>
                      ))}
                      <tr style={{ background: C.surface, borderTop: `2px solid ${C.border}` }}>
                        <td style={{ padding: '10px 12px', color: C.gold, fontWeight: 700 }}>Total Expenditure</td>
                        <td style={{ padding: '10px 12px', color: C.orange, textAlign: 'right', fontWeight: 700, fontSize: 15 }}>{fmt(ieData.totalExpense || 0)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {/* Net Position */}
                <div style={{ gridColumn: '1/-1', background: C.card, border: `2px solid ${(ieData.surplus || 0) >= 0 ? '#4ade80' : C.red}`, borderRadius: 12, padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontFamily: "'Cinzel', serif", fontSize: 16, color: C.goldLt }}>Net Surplus / (Deficit) for {year}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: (ieData.surplus || 0) >= 0 ? '#4ade80' : C.red }}>{fmt(Math.abs(ieData.surplus || 0))} {(ieData.surplus || 0) < 0 ? '(DEFICIT)' : 'SURPLUS'}</div>
                </div>
              </div>
            )
          )
        )}
      </div>
    );
  };

  // ── BUDGET MANAGEMENT ────────────────────────────────────────
  const BudgetPanel = () => {
    const [budgets, setBudgets]     = useState<any[]>([]);
    const [accounts, setAccounts]   = useState<any[]>([]);
    const [loading, setLoading]     = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [viewBudget, setViewBudget] = useState<any>(null);
    const [editBudget, setEditBudget] = useState<any>(null);
    const [flash, setFlash]         = useState('');
    const [flashType, setFlashType] = useState<'ok'|'err'>('ok');
    const [form, setForm] = useState({ title: '', description: '', period: 'ANNUAL', currency: 'NGN', lines: [] as any[] });
    const [newLine, setNewLine] = useState({ accountCodeId: '', description: '', category: 'EXPENSE', budgetedAmount: '', notes: '' });

    const showFlash = (msg: string, type: 'ok'|'err' = 'ok') => {
      setFlash(msg); setFlashType(type); setTimeout(() => setFlash(''), 4000);
    };

    const fetchAll = useCallback(async () => {
      setLoading(true);
      try {
        const [br, ar] = await Promise.all([
          fetch(`/api/finance/budgets?year=${year}`, { headers }),
          fetch('/api/finance/accounts', { headers }),
        ]);
        const bd = await br.json(); const ad = await ar.json();
        setBudgets(bd.budgets || []); setAccounts(ad.accounts || []);
      } catch { showFlash('Failed to load', 'err'); }
      finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const addLine = () => {
      if (!newLine.accountCodeId || !newLine.budgetedAmount) { showFlash('Select account and enter amount', 'err'); return; }
      const acct = accounts.find(a => a.id === newLine.accountCodeId);
      setForm(f => ({ ...f, lines: [...f.lines, { ...newLine, accountCode: acct }] }));
      setNewLine({ accountCodeId: '', description: '', category: 'EXPENSE', budgetedAmount: '', notes: '' }); showFlash('Line added — click Save Budget when done', 'ok');
    };

    const removeLine = (i: number) => setForm(f => ({ ...f, lines: f.lines.filter((_: any, idx: number) => idx !== i) }));

    const handleSave = async () => {
      if (!form.title) { showFlash('Title required', 'err'); return; }
      try {
        const url    = editBudget ? `/api/finance/budgets/${editBudget.id}` : '/api/finance/budgets';
        const method = editBudget ? 'PUT' : 'POST';
        const r = await fetch(url, { method, headers, body: JSON.stringify({ ...form, year, lines: form.lines.map((l: any) => ({ accountCodeId: l.accountCodeId, description: l.description, category: l.category, subcategory: l.subcategory || null, budgetedAmount: l.budgetedAmount, currency: l.currency || 'NGN', notes: l.notes || null })) }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash(editBudget ? 'Budget updated' : 'Budget created');
        setShowModal(false); setEditBudget(null); fetchAll();
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const handleAction = async (id: string, action: string, note = '') => {
      try {
        const userRole = (currentUser?.systemRole || currentUser?.role || '').toUpperCase();
        const r = await fetch(`/api/finance/budgets/${id}/action`, {
          method: 'POST', headers,
          body: JSON.stringify({ action, note, actorRole: userRole })
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash(`Budget ${action.toLowerCase()}d`);
        fetchAll();
        if (viewBudget) setViewBudget(d);
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const handleSyncActuals = async (id: string) => {
      try {
        const r = await fetch(`/api/finance/budgets/${id}/sync-actuals`, { method: 'POST', headers });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash('Actuals synced from posted journals');
        fetchAll(); if (viewBudget) setViewBudget(d);
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const handleDelete = async (id: string) => {
      if (!confirm('Delete this budget?')) return;
      try {
        const r = await fetch(`/api/finance/budgets/${id}`, { method: 'DELETE', headers });
        if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
        showFlash('Deleted'); fetchAll();
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const STATUS_COLORS: Record<string, string> = {
      DRAFT: C.muted, SUBMITTED: C.blue, UNDER_REVIEW: C.purple,
      APPROVED: '#4ade80', REJECTED: C.red, AMENDED: C.orange,
    };
    const STATUS_ICONS: Record<string, string> = {
      DRAFT:'📝', SUBMITTED:'📤', UNDER_REVIEW:'🔍', APPROVED:'✅', REJECTED:'❌', AMENDED:'🔄'
    };

    const incomeLines  = form.lines.filter((l: any) => l.category === 'INCOME');
    const expenseLines = form.lines.filter((l: any) => l.category === 'EXPENSE');
    const totalIncome  = incomeLines.reduce((s: number, l: any) => s + parseFloat(l.budgetedAmount || 0), 0);
    const totalExpense = expenseLines.reduce((s: number, l: any) => s + parseFloat(l.budgetedAmount || 0), 0);

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h2 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, fontSize: 18, margin: 0 }}>🎯 Budget Management — {year}</h2>
            <p style={{ color: C.muted, fontSize: 12, margin: '4px 0 0' }}>Workflow: CWC Drafts → Submits → BOT Reviews → BOT Approves</p>
          </div>
          <button onClick={() => { setEditBudget(null); setForm({ title: '', description: '', period: 'ANNUAL', currency: 'NGN', lines: [] }); setShowModal(true); }} style={btnPrimary}>+ New Budget</button>
        </div>

        {flash && <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 12, fontSize: 13, background: flashType === 'ok' ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${flashType === 'ok' ? '#4ade80' : C.red}`, color: flashType === 'ok' ? '#4ade80' : C.red }}>{flash}</div>}

        {/* Workflow Legend */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
          {Object.entries(STATUS_ICONS).map(([s, icon]) => (
            <span key={s} style={{ background: STATUS_COLORS[s] + '22', border: `1px solid ${STATUS_COLORS[s]}44`, color: STATUS_COLORS[s], borderRadius: 12, padding: '3px 10px', fontSize: 11 }}>{icon} {s}</span>
          ))}
        </div>

        {accounts.length === 0 && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#ef4444' }}>
            ⚠️ <strong>No Chart of Accounts found.</strong> Please go to the <strong>Chart of Accounts</strong> tab first and click "🌱 Seed Default COA" before creating a budget.
          </div>
        )}
        {loading ? <div style={{ textAlign: 'center', padding: 40, color: C.muted }}>Loading...</div> :
          budgets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>
              <div style={{ fontSize: 40 }}>🎯</div>
              <div style={{ marginBottom: 12 }}>No budgets for {year}</div>
              <button onClick={() => setShowModal(true)} style={btnPrimary}>Create First Budget</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 14 }}>
              {budgets.map(b => (
                <div key={b.id} style={{ background: C.card, border: `1px solid ${STATUS_COLORS[b.status]}44`, borderRadius: 12, padding: '18px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div>
                      <h3 style={{ color: C.goldLt, fontFamily: "'Cinzel', serif", fontSize: 15, margin: '0 0 4px' }}>{b.title}</h3>
                      <div style={{ color: C.muted, fontSize: 12 }}>{b.period} — Version {b.version} — {b.lines?.length || 0} line items</div>
                    </div>
                    <span style={{ background: STATUS_COLORS[b.status] + '22', border: `1px solid ${STATUS_COLORS[b.status]}44`, color: STATUS_COLORS[b.status], borderRadius: 12, padding: '4px 14px', fontSize: 12, fontWeight: 700 }}>
                      {STATUS_ICONS[b.status]} {b.status}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
                    {[
                      { label: 'Budgeted Income', value: fmt(b.totalIncome), color: C.teal },
                      { label: 'Budgeted Expense', value: fmt(b.totalExpense), color: C.orange },
                      { label: 'Net Position', value: fmt(b.netPosition), color: b.netPosition >= 0 ? '#4ade80' : C.red },
                    ].map(s => (
                      <div key={s.label} style={{ background: C.surface, borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => setViewBudget(b)} style={{ ...btnGhost, padding: '6px 14px', fontSize: 12 }}>👁 View Detail</button>
                    {['DRAFT','REJECTED'].includes(b.status) && (
                      <button onClick={() => { setEditBudget(b); setForm({ title: b.title, description: b.description||'', period: b.period, currency: b.currency, lines: b.lines.map((l: any) => ({ accountCodeId: l.accountCodeId, description: l.description, category: l.category, budgetedAmount: String(l.budgetedAmount), notes: l.notes||'', accountCode: l.accountCode })) }); setShowModal(true); }} style={{ ...btnGhost, padding: '6px 14px', fontSize: 12 }}>✏️ Edit</button>
                    )}
                    {b.status === 'DRAFT' && <button onClick={() => handleAction(b.id, 'SUBMIT')} style={{ ...btnPrimary, padding: '6px 14px', fontSize: 12 }}>📤 Submit to BOT</button>}
                    {b.status === 'SUBMITTED' && <button onClick={() => handleAction(b.id, 'REVIEW')} style={{ ...btnGhost, padding: '6px 14px', fontSize: 12 }}>🔍 Mark Under Review</button>}
                    {b.status === 'UNDER_REVIEW' && <>
                      <button onClick={() => handleAction(b.id, 'APPROVE')} style={{ ...btnPrimary, padding: '6px 14px', fontSize: 12 }}>✅ Approve</button>
                      <button onClick={() => { const note = prompt('Rejection reason:'); if (note) handleAction(b.id, 'REJECT', note); }} style={{ ...btnDanger, padding: '6px 14px' }}>❌ Reject</button>
                    </>}
                    {b.status === 'APPROVED' && <button onClick={() => handleSyncActuals(b.id)} style={{ ...btnGhost, padding: '6px 14px', fontSize: 12 }}>🔄 Sync Actuals</button>}
                    {['DRAFT','SUBMITTED'].includes(b.status) && <button onClick={() => handleDelete(b.id)} style={{ ...btnDanger, padding: '6px 14px' }}>🗑</button>}
                  </div>
                </div>
              ))}
            </div>
          )
        }

        {/* Budget View Modal */}
        {viewBudget && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 900, maxHeight: '95vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, margin: 0 }}>{viewBudget.title}</h2>
                  <p style={{ color: C.muted, fontSize: 13, margin: '4px 0 0' }}>{viewBudget.period} — {year} — Version {viewBudget.version}</p>
                </div>
                <span style={{ background: STATUS_COLORS[viewBudget.status] + '22', border: `1px solid ${STATUS_COLORS[viewBudget.status]}44`, color: STATUS_COLORS[viewBudget.status], borderRadius: 12, padding: '4px 14px', fontSize: 12 }}>{STATUS_ICONS[viewBudget.status]} {viewBudget.status}</span>
              </div>

              {/* Budget vs Actual Table */}
              <div style={{ background: C.surface, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
                <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ color: C.goldLt, margin: 0, fontSize: 14 }}>Budget vs Actual Analysis</h3>
                  <button onClick={() => handleSyncActuals(viewBudget.id)} style={{ ...btnGhost, padding: '4px 12px', fontSize: 11 }}>🔄 Sync Actuals</button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: C.card }}>
                      {['Account','Description','Category','Budgeted','Actual','Variance','Var %'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: ['Budgeted','Actual','Variance','Var %'].includes(h) ? 'right' : 'left', color: C.muted, fontSize: 11, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {['INCOME','EXPENSE'].map(cat => {
                      const items = viewBudget.lines?.filter((l: any) => l.category === cat) || [];
                      if (items.length === 0) return null;
                      return [
                        <tr key={`h-${cat}`} style={{ background: cat === 'INCOME' ? 'rgba(45,212,191,0.08)' : 'rgba(251,146,60,0.08)' }}>
                          <td colSpan={7} style={{ padding: '6px 12px', color: cat === 'INCOME' ? C.teal : C.orange, fontWeight: 700, fontSize: 11 }}>{cat}</td>
                        </tr>,
                        ...items.map((l: any, i: number) => {
                          const varPct = l.variancePct;
                          const varColor = Math.abs(varPct) < 5 ? '#4ade80' : Math.abs(varPct) < 20 ? C.gold : C.red;
                          return (
                            <tr key={l.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                              <td style={{ padding: '8px 12px', color: C.muted, fontFamily: 'monospace', fontSize: 11 }}>{l.accountCode?.code}</td>
                              <td style={{ padding: '8px 12px', color: C.text }}>{l.description}</td>
                              <td style={{ padding: '8px 12px', color: cat === 'INCOME' ? C.teal : C.orange, fontSize: 11 }}>{cat}</td>
                              <td style={{ padding: '8px 12px', color: C.text, textAlign: 'right' }}>₦{l.budgetedAmount.toLocaleString()}</td>
                              <td style={{ padding: '8px 12px', color: C.muted, textAlign: 'right' }}>₦{(l.actualAmount || 0).toLocaleString()}</td>
                              <td style={{ padding: '8px 12px', color: varColor, textAlign: 'right' }}>₦{Math.abs(l.variance || 0).toLocaleString()}</td>
                              <td style={{ padding: '8px 12px', color: varColor, textAlign: 'right' }}>{(l.variancePct || 0).toFixed(1)}%</td>
                            </tr>
                          );
                        })
                      ];
                    })}
                  </tbody>
                </table>
              </div>

              {/* Approval Trail */}
              {viewBudget.approvals?.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ color: C.goldLt, fontSize: 14, marginBottom: 12 }}>Approval Trail</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {viewBudget.approvals.map((a: any) => (
                      <div key={a.id} style={{ background: C.surface, borderRadius: 8, padding: '10px 14px', fontSize: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
                        <span style={{ color: C.gold, fontWeight: 700 }}>{a.action}</span>
                        <span style={{ color: C.muted }}>{a.actorEmail}</span>
                        <span style={{ color: C.muted }}>•</span>
                        <span style={{ color: C.muted }}>{new Date(a.timestamp).toLocaleString()}</span>
                        {a.note && <span style={{ color: C.text }}>— {a.note}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => setViewBudget(null)} style={btnPrimary}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 850, maxHeight: '95vh', overflowY: 'auto' }}>
              <h3 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, margin: '0 0 20px' }}>{editBudget ? '✏️ Edit Budget' : '🎯 New Budget'}</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 20 }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Budget Title *</label>
                  <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} style={inputStyle} placeholder="e.g. ERUE Festival 2026 — Annual Budget" />
                </div>
                <div>
                  <label style={labelStyle}>Period</label>
                  <select value={form.period} onChange={e => setForm(f => ({...f, period: e.target.value}))} style={inputStyle}>
                    {['ANNUAL','Q1','Q2','Q3','Q4','EVENT'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Currency</label>
                  <select value={form.currency} onChange={e => setForm(f => ({...f, currency: e.target.value}))} style={inputStyle}>
                    {['NGN','USD','GBP','EUR'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Description</label>
                  <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} style={inputStyle} placeholder="Optional" />
                </div>
              </div>

              {/* Budget Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
                {[
                  { label: 'Total Income', value: fmt(totalIncome), color: C.teal },
                  { label: 'Total Expense', value: fmt(totalExpense), color: C.orange },
                  { label: 'Net Position', value: fmt(totalIncome - totalExpense), color: totalIncome - totalExpense >= 0 ? '#4ade80' : C.red },
                ].map(s => (
                  <div key={s.label} style={{ background: C.surface, borderRadius: 8, padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Add Line */}
              <div style={{ background: C.surface, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <h4 style={{ color: C.gold, fontSize: 13, margin: '0 0 12px' }}>Add Budget Line</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={labelStyle}>Account Code</label>
                    <select value={newLine.accountCodeId} onChange={e => setNewLine(l => ({...l, accountCodeId: e.target.value}))} style={inputStyle}>
                      <option value="">-- Select account --</option>
                      {accounts.filter(a => a.isActive).map(a => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Category</label>
                    <select value={newLine.category} onChange={e => setNewLine(l => ({...l, category: e.target.value}))} style={inputStyle}>
                      <option value="INCOME">INCOME</option>
                      <option value="EXPENSE">EXPENSE</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Amount (₦)</label>
                    <input type="number" value={newLine.budgetedAmount} onChange={e => setNewLine(l => ({...l, budgetedAmount: e.target.value}))} style={inputStyle} placeholder="0.00" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button onClick={addLine} style={{ ...btnPrimary, width: '100%' }}>+ Add Line</button>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Line Description</label>
                  <input value={newLine.description} onChange={e => setNewLine(l => ({...l, description: e.target.value}))} style={inputStyle} placeholder="Description for this line" />
                </div>
              </div>

              {/* Budget Lines */}
              {form.lines.length > 0 && (
                <div style={{ background: C.surface, borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        {['Account','Description','Category','Amount',''].map(h => (
                          <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Amount' ? 'right' : 'left', color: C.muted, fontSize: 11, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {form.lines.map((l: any, i: number) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: '8px 12px', color: C.blue, fontFamily: 'monospace', fontSize: 11 }}>{l.accountCode?.code}</td>
                          <td style={{ padding: '8px 12px', color: C.text }}>{l.description || l.accountCode?.name}</td>
                          <td style={{ padding: '8px 12px', color: l.category === 'INCOME' ? C.teal : C.orange, fontSize: 11 }}>{l.category}</td>
                          <td style={{ padding: '8px 12px', color: C.text, textAlign: 'right' }}>₦{parseFloat(l.budgetedAmount).toLocaleString()}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <button onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 18 }}>×</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => { setShowModal(false); setEditBudget(null); }} style={btnGhost}>Cancel</button>
                <button onClick={handleSave} style={btnPrimary}>{editBudget ? 'Update Budget' : 'Save Budget'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── WORKFLOW TASKS ───────────────────────────────────────────
  const TasksPanel = () => {
    const [tasks, setTasks]         = useState<any[]>([]);
    const [loading, setLoading]     = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editTask, setEditTask]   = useState<any>(null);
    const [flash, setFlash]         = useState('');
    const [flashType, setFlashType] = useState<'ok'|'err'>('ok');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [form, setForm] = useState({ title:'', description:'', category:'GENERAL', priority:'MEDIUM', assignedTo:'', dueDate:'', notes:'' });

    const showFlash = (msg: string, type: 'ok'|'err' = 'ok') => {
      setFlash(msg); setFlashType(type); setTimeout(() => setFlash(''), 4000);
    };

    const fetchTasks = useCallback(async () => {
      setLoading(true);
      try {
        let url = `/api/workflow/tasks?year=${year}`;
        if (filterStatus)   url += `&status=${filterStatus}`;
        if (filterCategory) url += `&category=${filterCategory}`;
        const r = await fetch(url, { headers });
        const d = await r.json();
        setTasks(d.tasks || []);
      } catch { showFlash('Failed to load', 'err'); }
      finally { setLoading(false); }
    }, [filterStatus, filterCategory]);

    useEffect(() => { fetchTasks(); }, [fetchTasks]);

    const handleSave = async () => {
      if (!form.title) { showFlash('Title required', 'err'); return; }
      try {
        const url    = editTask ? `/api/workflow/tasks/${editTask.id}` : '/api/workflow/tasks';
        const method = editTask ? 'PUT' : 'POST';
        const r = await fetch(url, { method, headers, body: JSON.stringify({ ...form, festivalYear: year }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash(editTask ? 'Task updated' : 'Task created');
        setShowModal(false); setEditTask(null); fetchTasks();
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const handleStatusChange = async (id: string, status: string) => {
      try {
        const r = await fetch(`/api/workflow/tasks/${id}`, { method: 'PUT', headers, body: JSON.stringify({ status }) });
        if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
        showFlash('Status updated'); fetchTasks();
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const handleDelete = async (id: string) => {
      if (!confirm('Delete this task?')) return;
      try {
        await fetch(`/api/workflow/tasks/${id}`, { method: 'DELETE', headers });
        showFlash('Deleted'); fetchTasks();
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const CATEGORIES = ['GENERAL','FINANCE','LOC','CWC','BOT','MEDIA','VENDOR','LOGISTICS'];
    const PRIORITIES  = ['LOW','MEDIUM','HIGH','URGENT'];
    const STATUSES    = ['PENDING','IN_PROGRESS','BLOCKED','COMPLETED','CANCELLED'];
    const PRIORITY_COLORS: Record<string, string> = { LOW: C.muted, MEDIUM: C.blue, HIGH: C.orange, URGENT: C.red };
    const STATUS_COLORS: Record<string, string>   = { PENDING: C.muted, IN_PROGRESS: C.blue, BLOCKED: C.red, COMPLETED: '#4ade80', CANCELLED: C.muted };

    const stats = {
      total:     tasks.length,
      pending:   tasks.filter(t => t.status === 'PENDING').length,
      progress:  tasks.filter(t => t.status === 'IN_PROGRESS').length,
      completed: tasks.filter(t => t.status === 'COMPLETED').length,
      overdue:   tasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && !['COMPLETED','CANCELLED'].includes(t.status)).length,
    };

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
          <h2 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, fontSize: 18, margin: 0 }}>✅ Workflow Tasks</h2>
          <button onClick={() => { setEditTask(null); setForm({ title:'',description:'',category:'GENERAL',priority:'MEDIUM',assignedTo:'',dueDate:'',notes:'' }); setShowModal(true); }} style={btnPrimary}>+ New Task</button>
        </div>

        {flash && <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 12, fontSize: 13, background: flashType === 'ok' ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${flashType === 'ok' ? '#4ade80' : C.red}`, color: flashType === 'ok' ? '#4ade80' : C.red }}>{flash}</div>}

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Total', value: stats.total, color: C.text },
            { label: 'Pending', value: stats.pending, color: C.muted },
            { label: 'In Progress', value: stats.progress, color: C.blue },
            { label: 'Completed', value: stats.completed, color: '#4ade80' },
            { label: 'Overdue', value: stats.overdue, color: C.red },
          ].map(s => (
            <div key={s.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, maxWidth: 160 }}>
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={{ ...inputStyle, maxWidth: 160 }}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={fetchTasks} style={btnGhost}>🔄</button>
        </div>

        {loading ? <div style={{ textAlign: 'center', padding: 40, color: C.muted }}>Loading...</div> :
          tasks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>
              <div style={{ fontSize: 40 }}>✅</div>
              <div>No tasks found</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {tasks.map(t => {
                const isOverdue = t.dueDate && new Date(t.dueDate) < new Date() && !['COMPLETED','CANCELLED'].includes(t.status);
                return (
                  <div key={t.id} style={{ background: C.card, border: `1px solid ${isOverdue ? C.red : C.border}`, borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: C.text, fontSize: 14 }}>{t.title}</span>
                        <span style={{ background: PRIORITY_COLORS[t.priority] + '22', color: PRIORITY_COLORS[t.priority], borderRadius: 10, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>{t.priority}</span>
                        <span style={{ background: C.surface, color: C.muted, borderRadius: 10, padding: '1px 8px', fontSize: 10 }}>{t.category}</span>
                        {isOverdue && <span style={{ background: C.red + '22', color: C.red, borderRadius: 10, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>OVERDUE</span>}
                      </div>
                      {t.description && <div style={{ color: C.muted, fontSize: 12, marginBottom: 6 }}>{t.description}</div>}
                      <div style={{ display: 'flex', gap: 12, fontSize: 11, color: C.muted, flexWrap: 'wrap' }}>
                        {t.assignedTo && <span>👤 {t.assignedTo}</span>}
                        {t.dueDate && <span style={{ color: isOverdue ? C.red : C.muted }}>📅 {new Date(t.dueDate).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <select value={t.status} onChange={e => handleStatusChange(t.id, e.target.value)}
                        style={{ ...inputStyle, width: 'auto', padding: '4px 8px', fontSize: 11, color: STATUS_COLORS[t.status] }}>
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => { setEditTask(t); setForm({ title:t.title, description:t.description||'', category:t.category, priority:t.priority, assignedTo:t.assignedTo||'', dueDate:t.dueDate ? t.dueDate.split('T')[0] : '', notes:t.notes||'' }); setShowModal(true); }} style={{ ...btnGhost, padding: '4px 8px', fontSize: 11 }}>✏️</button>
                      <button onClick={() => handleDelete(t.id)} style={{ ...btnDanger, padding: '4px 8px' }}>🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        }

        {showModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 560 }}>
              <h3 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, margin: '0 0 20px' }}>{editTask ? '✏️ Edit Task' : '➕ New Task'}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Task Title *</label>
                  <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} style={inputStyle} placeholder="Task title" />
                </div>
                <div>
                  <label style={labelStyle}>Category</label>
                  <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} style={inputStyle}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Priority</label>
                  <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))} style={inputStyle}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Assigned To</label>
                  <input value={form.assignedTo} onChange={e => setForm(f => ({...f, assignedTo: e.target.value}))} style={inputStyle} placeholder="Name or email" />
                </div>
                <div>
                  <label style={labelStyle}>Due Date</label>
                  <input type="date" value={form.dueDate} onChange={e => setForm(f => ({...f, dueDate: e.target.value}))} style={inputStyle} />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Description</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Task details" />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Notes</label>
                  <input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} style={inputStyle} placeholder="Optional notes" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button onClick={() => { setShowModal(false); setEditTask(null); }} style={btnGhost}>Cancel</button>
                <button onClick={handleSave} style={btnPrimary}>{editTask ? 'Update' : 'Create Task'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── MEETING MINUTES ──────────────────────────────────────────
  const MinutesPanel = () => {
    const [minutes, setMinutes]     = useState<any[]>([]);
    const [loading, setLoading]     = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [viewItem, setViewItem]   = useState<any>(null);
    const [editItem, setEditItem]   = useState<any>(null);
    const [flash, setFlash]         = useState('');
    const [flashType, setFlashType] = useState<'ok'|'err'>('ok');
    const [filterCommittee, setFilterCommittee] = useState('');
    const [newAttendee, setNewAttendee] = useState('');
    const [newAgendaItem, setNewAgendaItem] = useState('');
    const [newActionItem, setNewActionItem] = useState({ item:'', owner:'', dueDate:'' });
    const [form, setForm] = useState({
      title:'', committee:'CWC', meetingDate: new Date().toISOString().split('T')[0],
      venue:'', chairperson:'', secretary:'', minutesText:'',
      attendees:[] as string[], apologies:[] as string[], agenda:[] as string[], actionItems:[] as any[],
    });

    const showFlash = (msg: string, type: 'ok'|'err' = 'ok') => {
      setFlash(msg); setFlashType(type); setTimeout(() => setFlash(''), 4000);
    };

    const fetchMinutes = useCallback(async () => {
      setLoading(true);
      try {
        let url = `/api/minutes?year=${year}`;
        if (filterCommittee) url += `&committee=${filterCommittee}`;
        const r = await fetch(url, { headers });
        const d = await r.json();
        setMinutes(d.minutes || []);
      } catch { showFlash('Failed to load', 'err'); }
      finally { setLoading(false); }
    }, [filterCommittee]);

    useEffect(() => { fetchMinutes(); }, [fetchMinutes]);

    const handleSave = async () => {
      if (!form.title || !form.committee || !form.meetingDate) { showFlash('Title, committee, date required', 'err'); return; }
      try {
        const url    = editItem ? `/api/minutes/${editItem.id}` : '/api/minutes';
        const method = editItem ? 'PUT' : 'POST';
        const r = await fetch(url, { method, headers, body: JSON.stringify({ ...form, festivalYear: year }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash(editItem ? 'Minutes updated' : 'Minutes created');
        setShowModal(false); setEditItem(null); fetchMinutes();
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const handleStatusChange = async (id: string, status: string) => {
      try {
        const r = await fetch(`/api/minutes/${id}`, { method: 'PUT', headers, body: JSON.stringify({ status }) });
        if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
        showFlash('Status updated'); fetchMinutes();
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const handleDelete = async (id: string) => {
      if (!confirm('Delete these minutes?')) return;
      try {
        await fetch(`/api/minutes/${id}`, { method: 'DELETE', headers });
        showFlash('Deleted'); fetchMinutes();
      } catch (e: any) { showFlash(e.message, 'err'); }
    };

    const COMMITTEES = ['CWC','BOT','LOC','JOINT','GENERAL'];
    const STATUSES   = ['DRAFT','CIRCULATED','CONFIRMED','ARCHIVED'];
    const STATUS_COLORS: Record<string, string> = { DRAFT: C.muted, CIRCULATED: C.blue, CONFIRMED: '#4ade80', ARCHIVED: C.purple };

    const openNew = () => {
      setEditItem(null);
      setForm({ title:'', committee:'CWC', meetingDate: new Date().toISOString().split('T')[0], venue:'', chairperson:'', secretary:'', minutesText:'', attendees:[], apologies:[], agenda:[], actionItems:[] });
      setShowModal(true);
    };

    const openEdit = (m: any) => {
      setEditItem(m);
      setForm({ title:m.title, committee:m.committee, meetingDate:m.meetingDate.split('T')[0], venue:m.venue||'', chairperson:m.chairperson||'', secretary:m.secretary||'', minutesText:m.minutesText, attendees:[...m.attendees], apologies:[...m.apologies], agenda:[...m.agenda], actionItems:[...m.actionItems] });
      setShowModal(true);
      setViewItem(null);
    };

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
          <h2 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, fontSize: 18, margin: 0 }}>📋 Meeting Minutes</h2>
          <button onClick={openNew} style={btnPrimary}>+ New Minutes</button>
        </div>

        {flash && <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 12, fontSize: 13, background: flashType === 'ok' ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${flashType === 'ok' ? '#4ade80' : C.red}`, color: flashType === 'ok' ? '#4ade80' : C.red }}>{flash}</div>}

        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <select value={filterCommittee} onChange={e => setFilterCommittee(e.target.value)} style={{ ...inputStyle, maxWidth: 180 }}>
            <option value="">All Committees</option>
            {COMMITTEES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={fetchMinutes} style={btnGhost}>🔄</button>
        </div>

        {loading ? <div style={{ textAlign: 'center', padding: 40, color: C.muted }}>Loading...</div> :
          minutes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>
              <div style={{ fontSize: 40 }}>📋</div>
              <div>No minutes recorded</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {minutes.map(m => (
                <div key={m.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, fontSize: 14 }}>{m.title}</div>
                      <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>
                        {m.committee} Committee — {new Date(m.meetingDate).toLocaleDateString()} — {m.attendees?.length || 0} attendees
                      </div>
                    </div>
                    <span style={{ background: STATUS_COLORS[m.status] + '22', border: `1px solid ${STATUS_COLORS[m.status]}44`, color: STATUS_COLORS[m.status], borderRadius: 12, padding: '3px 12px', fontSize: 11, fontWeight: 700 }}>{m.status}</span>
                  </div>
                  {m.venue && <div style={{ color: C.muted, fontSize: 12, marginBottom: 8 }}>📍 {m.venue}</div>}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => setViewItem(m)} style={{ ...btnGhost, padding: '5px 12px', fontSize: 12 }}>👁 View</button>
                    <button onClick={() => openEdit(m)} style={{ ...btnGhost, padding: '5px 12px', fontSize: 12 }}>✏️ Edit</button>
                    {m.status === 'DRAFT' && <button onClick={() => handleStatusChange(m.id, 'CIRCULATED')} style={{ ...btnPrimary, padding: '5px 12px', fontSize: 12 }}>📤 Circulate</button>}
                    {m.status === 'CIRCULATED' && <button onClick={() => handleStatusChange(m.id, 'CONFIRMED')} style={{ ...btnPrimary, padding: '5px 12px', fontSize: 12 }}>✅ Confirm</button>}
                    {m.status === 'CONFIRMED' && <button onClick={() => handleStatusChange(m.id, 'ARCHIVED')} style={{ ...btnGhost, padding: '5px 12px', fontSize: 12 }}>📁 Archive</button>}
                    <button onClick={() => handleDelete(m.id)} style={{ ...btnDanger, padding: '5px 12px' }}>🗑</button>
                  </div>
                </div>
              ))}
            </div>
          )
        }

        {/* View Modal */}
        {viewItem && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 750, maxHeight: '95vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, margin: 0 }}>{viewItem.title}</h2>
                  <p style={{ color: C.muted, fontSize: 13, margin: '4px 0 0' }}>{viewItem.committee} — {new Date(viewItem.meetingDate).toLocaleDateString()}</p>
                </div>
                <button onClick={() => viewItem && window.print()} style={{ ...btnGhost, padding: '6px 12px', fontSize: 12 }}>🖨 Print</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 13, marginBottom: 20 }}>
                {[
                  ['Venue', viewItem.venue || '—'],
                  ['Chairperson', viewItem.chairperson || '—'],
                  ['Secretary', viewItem.secretary || '—'],
                  ['Status', viewItem.status],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: C.muted, minWidth: 90 }}>{k}</span>
                    <span style={{ color: C.text }}>{v}</span>
                  </div>
                ))}
              </div>

              {viewItem.attendees?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ color: C.gold, fontSize: 13, margin: '0 0 8px' }}>Attendees ({viewItem.attendees.length})</h4>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {viewItem.attendees.map((a: string) => <span key={a} style={{ background: C.surface, borderRadius: 12, padding: '2px 10px', fontSize: 12, color: C.text }}>{a}</span>)}
                  </div>
                </div>
              )}

              {viewItem.agenda?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ color: C.gold, fontSize: 13, margin: '0 0 8px' }}>Agenda</h4>
                  <ol style={{ color: C.text, fontSize: 13, paddingLeft: 20, margin: 0 }}>
                    {viewItem.agenda.map((a: string, i: number) => <li key={i} style={{ marginBottom: 4 }}>{a}</li>)}
                  </ol>
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <h4 style={{ color: C.gold, fontSize: 13, margin: '0 0 8px' }}>Minutes</h4>
                <div style={{ background: C.surface, borderRadius: 8, padding: 14, fontSize: 13, color: C.text, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{viewItem.minutesText || '—'}</div>
              </div>

              {viewItem.actionItems?.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <h4 style={{ color: C.gold, fontSize: 13, margin: '0 0 8px' }}>Action Items</h4>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {viewItem.actionItems.map((a: any, i: number) => (
                      <div key={i} style={{ background: C.surface, borderRadius: 8, padding: '10px 14px', fontSize: 13, display: 'flex', gap: 12 }}>
                        <span style={{ color: C.gold, minWidth: 24 }}>{i+1}.</span>
                        <div>
                          <div style={{ color: C.text }}>{a.item}</div>
                          {a.owner && <div style={{ color: C.muted, fontSize: 12 }}>👤 {a.owner} {a.dueDate ? `— By ${new Date(a.dueDate).toLocaleDateString()}` : ''}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => openEdit(viewItem)} style={btnGhost}>✏️ Edit</button>
                <button onClick={() => setViewItem(null)} style={btnPrimary}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 760, maxHeight: '95vh', overflowY: 'auto' }}>
              <h3 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, margin: '0 0 20px' }}>{editItem ? '✏️ Edit Minutes' : '📋 New Meeting Minutes'}</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Title *</label>
                  <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} style={inputStyle} placeholder="e.g. CWC Meeting — October 2026" />
                </div>
                <div>
                  <label style={labelStyle}>Committee *</label>
                  <select value={form.committee} onChange={e => setForm(f => ({...f, committee: e.target.value}))} style={inputStyle}>
                    {COMMITTEES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Meeting Date *</label>
                  <input type="date" value={form.meetingDate} onChange={e => setForm(f => ({...f, meetingDate: e.target.value}))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Venue</label>
                  <input value={form.venue} onChange={e => setForm(f => ({...f, venue: e.target.value}))} style={inputStyle} placeholder="Meeting venue" />
                </div>
                <div>
                  <label style={labelStyle}>Chairperson</label>
                  <input value={form.chairperson} onChange={e => setForm(f => ({...f, chairperson: e.target.value}))} style={inputStyle} placeholder="Name" />
                </div>
                <div>
                  <label style={labelStyle}>Secretary</label>
                  <input value={form.secretary} onChange={e => setForm(f => ({...f, secretary: e.target.value}))} style={inputStyle} placeholder="Name" />
                </div>

                {/* Attendees */}
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Attendees</label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input value={newAttendee} onChange={e => setNewAttendee(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (newAttendee.trim()) { setForm(f => ({...f, attendees: [...f.attendees, newAttendee.trim()]})); setNewAttendee(''); }}}}
                      style={{ ...inputStyle, flex: 1 }} placeholder="Name + Enter" />
                    <button onClick={() => { if (newAttendee.trim()) { setForm(f => ({...f, attendees: [...f.attendees, newAttendee.trim()]})); setNewAttendee(''); }}} style={btnGhost}>Add</button>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {form.attendees.map((a, i) => (
                      <span key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '2px 10px', fontSize: 12, color: C.text, display: 'flex', gap: 6, alignItems: 'center' }}>
                        {a} <button onClick={() => setForm(f => ({...f, attendees: f.attendees.filter((_, idx) => idx !== i)}))} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', padding: 0, fontSize: 14 }}>×</button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Agenda */}
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Agenda Items</label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input value={newAgendaItem} onChange={e => setNewAgendaItem(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (newAgendaItem.trim()) { setForm(f => ({...f, agenda: [...f.agenda, newAgendaItem.trim()]})); setNewAgendaItem(''); }}}}
                      style={{ ...inputStyle, flex: 1 }} placeholder="Agenda item + Enter" />
                    <button onClick={() => { if (newAgendaItem.trim()) { setForm(f => ({...f, agenda: [...f.agenda, newAgendaItem.trim()]})); setNewAgendaItem(''); }}} style={btnGhost}>Add</button>
                  </div>
                  <ol style={{ paddingLeft: 20, margin: 0 }}>
                    {form.agenda.map((a, i) => (
                      <li key={i} style={{ color: C.text, fontSize: 13, marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                        {a} <button onClick={() => setForm(f => ({...f, agenda: f.agenda.filter((_, idx) => idx !== i)}))} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer' }}>×</button>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Minutes Text */}
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Minutes Text *</label>
                  <textarea value={form.minutesText} onChange={e => setForm(f => ({...f, minutesText: e.target.value}))} rows={8} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.7 }} placeholder="Record the full minutes of the meeting here..." />
                </div>

                {/* Action Items */}
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Action Items</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, marginBottom: 8 }}>
                    <input value={newActionItem.item} onChange={e => setNewActionItem(ai => ({...ai, item: e.target.value}))} style={inputStyle} placeholder="Action item" />
                    <input value={newActionItem.owner} onChange={e => setNewActionItem(ai => ({...ai, owner: e.target.value}))} style={inputStyle} placeholder="Owner" />
                    <input type="date" value={newActionItem.dueDate} onChange={e => setNewActionItem(ai => ({...ai, dueDate: e.target.value}))} style={inputStyle} />
                    <button onClick={() => { if (newActionItem.item.trim()) { setForm(f => ({...f, actionItems: [...f.actionItems, {...newActionItem}]})); setNewActionItem({item:'',owner:'',dueDate:''}); }}} style={btnPrimary}>+</button>
                  </div>
                  {form.actionItems.map((a, i) => (
                    <div key={i} style={{ background: C.surface, borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: C.text }}>{i+1}. {a.item} — <span style={{ color: C.muted }}>{a.owner}</span></span>
                      <button onClick={() => setForm(f => ({...f, actionItems: f.actionItems.filter((_, idx) => idx !== i)}))} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer' }}>×</button>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => { setShowModal(false); setEditItem(null); }} style={btnGhost}>Cancel</button>
                <button onClick={handleSave} style={btnPrimary}>{editItem ? 'Update Minutes' : 'Save Minutes'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };


  // ── BANKING PANEL ───────────────────────────────────────────
  const BankingPanel = () => {
    const [accounts, setAccounts]   = useState<any[]>([]);
    const [selAccount, setSelAccount] = useState<any>(null);
    const [transactions, setTxns]   = useState<any[]>([]);
    const [loading, setLoading]     = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showTxModal, setShowTxModal] = useState(false);
    const [editAcct, setEditAcct]   = useState<any>(null);
    const [flash, setFlash]         = useState('');
    const [flashType, setFlashType] = useState<'ok'|'err'>('ok');
    const [form, setForm] = useState({ accountName:'', accountNumber:'', bankName:'', bankBranch:'', sortCode:'', currency:'NGN', openingBalance:'0', isPrimary:false, notes:'' });
    const [txForm, setTxForm] = useState({ date: new Date().toISOString().split('T')[0], reference:'', description:'', type:'CREDIT', amount:'', category:'RECEIPT', notes:'' });

    const showFlash = (msg:string, type:'ok'|'err'='ok') => { setFlash(msg); setFlashType(type); setTimeout(()=>setFlash(''),4000); };

    const fetchAccounts = useCallback(async () => {
      setLoading(true);
      try {
        const r = await fetch('/api/finance/bank-accounts', { headers });
        const d = await r.json();
        setAccounts(d.accounts || []);
      } catch { showFlash('Failed to load','err'); }
      finally { setLoading(false); }
    },[]);

    const fetchTxns = useCallback(async (id:string) => {
      try {
        const r = await fetch(`/api/finance/bank-accounts/${id}/transactions`, { headers });
        const d = await r.json();
        setTxns(d.transactions || []);
      } catch { showFlash('Failed to load transactions','err'); }
    },[]);

    useEffect(() => { fetchAccounts(); },[fetchAccounts]);
    useEffect(() => { if(selAccount) fetchTxns(selAccount.id); },[selAccount]);

    const handleSaveAccount = async () => {
      try {
        const url = editAcct ? `/api/finance/bank-accounts/${editAcct.id}` : '/api/finance/bank-accounts';
        const method = editAcct ? 'PUT' : 'POST';
        const r = await fetch(url, { method, headers, body: JSON.stringify(form) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash(editAcct ? 'Account updated' : 'Account added');
        setShowModal(false); fetchAccounts();
      } catch(e:any) { showFlash(e.message,'err'); }
    };

    const handleAddTx = async () => {
      if (!selAccount) return;
      try {
        const r = await fetch(`/api/finance/bank-accounts/${selAccount.id}/transactions`, { method:'POST', headers, body: JSON.stringify(txForm) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash('Transaction recorded'); setShowTxModal(false);
        fetchAccounts(); fetchTxns(selAccount.id);
      } catch(e:any) { showFlash(e.message,'err'); }
    };

    const handleDeleteTx = async (txId:string) => {
      if (!selAccount || !confirm('Delete this transaction?')) return;
      try {
        const r = await fetch(`/api/finance/bank-accounts/${selAccount.id}/transactions/${txId}`, { method:'DELETE', headers });
        if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
        showFlash('Deleted'); fetchAccounts(); fetchTxns(selAccount.id);
      } catch(e:any) { showFlash(e.message,'err'); }
    };

    const totalBalance = accounts.reduce((s,a) => s + a.currentBalance, 0);

    return (
      <div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:10 }}>
          <div>
            <h2 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, fontSize:18, margin:0 }}>🏦 Banking & Cash Management</h2>
            <p style={{ color:C.muted, fontSize:12, margin:'4px 0 0' }}>Total Balance: <strong style={{ color:C.teal, fontSize:16 }}>₦{totalBalance.toLocaleString()}</strong></p>
          </div>
          <button onClick={()=>{ setEditAcct(null); setForm({accountName:'',accountNumber:'',bankName:'',bankBranch:'',sortCode:'',currency:'NGN',openingBalance:'0',isPrimary:false,notes:''}); setShowModal(true); }} style={btnPrimary}>+ Add Bank Account</button>
        </div>

        {flash && <div style={{ padding:'10px 16px', borderRadius:8, marginBottom:12, fontSize:13, background:flashType==='ok'?'rgba(74,222,128,0.1)':'rgba(239,68,68,0.1)', border:`1px solid ${flashType==='ok'?'#4ade80':C.red}`, color:flashType==='ok'?'#4ade80':C.red }}>{flash}</div>}

        <div style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:20 }}>
          {/* Account list */}
          <div>
            <h3 style={{ color:C.goldLt, fontSize:13, marginBottom:12 }}>Bank Accounts</h3>
            {loading ? <div style={{ color:C.muted, fontSize:13 }}>Loading...</div> :
              accounts.length === 0 ? <div style={{ color:C.muted, fontSize:13, textAlign:'center', padding:30 }}>No accounts yet<br/><button onClick={()=>setShowModal(true)} style={{ ...btnPrimary, marginTop:10, fontSize:12 }}>Add Account</button></div> :
              accounts.map(a => (
                <div key={a.id} onClick={()=>setSelAccount(a)} style={{ background: selAccount?.id===a.id ? C.surface : C.card, border:`1px solid ${selAccount?.id===a.id ? C.gold : C.border}`, borderRadius:10, padding:'12px 14px', cursor:'pointer', marginBottom:8 }}>
                  <div style={{ color:C.goldLt, fontSize:13, fontWeight:600 }}>{a.accountName}</div>
                  <div style={{ color:C.muted, fontSize:11 }}>{a.bankName} — {a.accountNumber}</div>
                  <div style={{ color:C.teal, fontSize:15, fontWeight:700, marginTop:4 }}>₦{a.currentBalance.toLocaleString()}</div>
                  {a.isPrimary && <span style={{ background:C.gold+'22', color:C.gold, borderRadius:10, padding:'1px 8px', fontSize:10 }}>PRIMARY</span>}
                  <div style={{ display:'flex', gap:6, marginTop:8 }}>
                    <button onClick={e=>{e.stopPropagation();setEditAcct(a);setForm({accountName:a.accountName,accountNumber:a.accountNumber,bankName:a.bankName,bankBranch:a.bankBranch||'',sortCode:a.sortCode||'',currency:a.currency,openingBalance:String(a.openingBalance),isPrimary:a.isPrimary,notes:a.notes||''});setShowModal(true);}} style={{...btnGhost,padding:'3px 8px',fontSize:11}}>✏️</button>
                  </div>
                </div>
              ))
            }
          </div>

          {/* Transactions */}
          <div>
            {!selAccount ? (
              <div style={{ textAlign:'center', padding:60, color:C.muted }}>
                <div style={{ fontSize:40 }}>🏦</div>
                <div>Select a bank account to view transactions</div>
              </div>
            ) : (
              <>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                  <h3 style={{ color:C.goldLt, fontSize:14, margin:0 }}>{selAccount.accountName} — Transactions</h3>
                  <button onClick={()=>{ setTxForm({date:new Date().toISOString().split('T')[0],reference:'',description:'',type:'CREDIT',amount:'',category:'RECEIPT',notes:''}); setShowTxModal(true); }} style={btnPrimary}>+ Record Transaction</button>
                </div>
                {transactions.length === 0 ? <div style={{ textAlign:'center', padding:40, color:C.muted }}>No transactions recorded</div> : (
                  <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                      <thead>
                        <tr style={{ background:C.surface }}>
                          {['Date','Reference','Description','Type','Amount','Balance',''].map(h=>(
                            <th key={h} style={{ padding:'8px 12px', textAlign:['Amount','Balance'].includes(h)?'right':'left', color:C.muted, fontSize:11, borderBottom:`1px solid ${C.border}` }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((t,i)=>(
                          <tr key={t.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                            <td style={{ padding:'8px 12px', color:C.muted, fontSize:12 }}>{new Date(t.date).toLocaleDateString()}</td>
                            <td style={{ padding:'8px 12px', color:C.blue, fontFamily:'monospace', fontSize:11 }}>{t.reference||'—'}</td>
                            <td style={{ padding:'8px 12px', color:C.text, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.description}</td>
                            <td style={{ padding:'8px 12px' }}><span style={{ color:t.type==='CREDIT'?'#4ade80':C.red, fontWeight:700, fontSize:11 }}>{t.type}</span></td>
                            <td style={{ padding:'8px 12px', color:t.type==='CREDIT'?'#4ade80':C.red, textAlign:'right', fontWeight:600 }}>₦{t.amount.toLocaleString()}</td>
                            <td style={{ padding:'8px 12px', color:C.text, textAlign:'right' }}>₦{t.balance.toLocaleString()}</td>
                            <td style={{ padding:'8px 12px' }}>
                              {!t.isReconciled && <button onClick={()=>handleDeleteTx(t.id)} style={{ ...btnDanger, padding:'3px 8px' }}>🗑</button>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Add Account Modal */}
        {showModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:28, width:'100%', maxWidth:540 }}>
              <h3 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, margin:'0 0 20px' }}>{editAcct?'✏️ Edit Account':'🏦 Add Bank Account'}</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Account Name *</label><input value={form.accountName} onChange={e=>setForm(f=>({...f,accountName:e.target.value}))} style={inputStyle} placeholder="e.g. GTBank Festival Account" /></div>
                <div><label style={labelStyle}>Account Number *</label><input value={form.accountNumber} onChange={e=>setForm(f=>({...f,accountNumber:e.target.value}))} style={inputStyle} placeholder="0123456789" /></div>
                <div><label style={labelStyle}>Bank Name *</label><input value={form.bankName} onChange={e=>setForm(f=>({...f,bankName:e.target.value}))} style={inputStyle} placeholder="Guaranty Trust Bank" /></div>
                <div><label style={labelStyle}>Branch</label><input value={form.bankBranch} onChange={e=>setForm(f=>({...f,bankBranch:e.target.value}))} style={inputStyle} placeholder="Branch name" /></div>
                <div><label style={labelStyle}>Sort Code</label><input value={form.sortCode} onChange={e=>setForm(f=>({...f,sortCode:e.target.value}))} style={inputStyle} placeholder="058" /></div>
                <div><label style={labelStyle}>Currency</label><select value={form.currency} onChange={e=>setForm(f=>({...f,currency:e.target.value}))} style={inputStyle}>{['NGN','USD','GBP','EUR'].map(c=><option key={c} value={c}>{c}</option>)}</select></div>
                {!editAcct && <div><label style={labelStyle}>Opening Balance</label><input type="number" value={form.openingBalance} onChange={e=>setForm(f=>({...f,openingBalance:e.target.value}))} style={inputStyle} /></div>}
                <div style={{ display:'flex', alignItems:'center', gap:8 }}><input type="checkbox" checked={form.isPrimary} onChange={e=>setForm(f=>({...f,isPrimary:e.target.checked}))} /><span style={{ color:C.text, fontSize:13 }}>Primary Account</span></div>
                <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Notes</label><input value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={inputStyle} placeholder="Optional" /></div>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
                <button onClick={()=>setShowModal(false)} style={btnGhost}>Cancel</button>
                <button onClick={handleSaveAccount} style={btnPrimary}>{editAcct?'Update':'Add Account'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Record Transaction Modal */}
        {showTxModal && selAccount && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:28, width:'100%', maxWidth:500 }}>
              <h3 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, margin:'0 0 20px' }}>Record Transaction — {selAccount.accountName}</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <div><label style={labelStyle}>Date *</label><input type="date" value={txForm.date} onChange={e=>setTxForm(f=>({...f,date:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Type *</label><select value={txForm.type} onChange={e=>setTxForm(f=>({...f,type:e.target.value}))} style={inputStyle}><option value="CREDIT">CREDIT (Receipt)</option><option value="DEBIT">DEBIT (Payment)</option></select></div>
                <div><label style={labelStyle}>Amount (₦) *</label><input type="number" value={txForm.amount} onChange={e=>setTxForm(f=>({...f,amount:e.target.value}))} style={inputStyle} placeholder="0.00" /></div>
                <div><label style={labelStyle}>Category</label><select value={txForm.category} onChange={e=>setTxForm(f=>({...f,category:e.target.value}))} style={inputStyle}>{['RECEIPT','PAYMENT','TRANSFER','CHARGE','INTEREST'].map(c=><option key={c} value={c}>{c}</option>)}</select></div>
                <div><label style={labelStyle}>Reference</label><input value={txForm.reference} onChange={e=>setTxForm(f=>({...f,reference:e.target.value}))} style={inputStyle} placeholder="Cheque/transfer ref" /></div>
                <div><label style={labelStyle}>Journal Ref</label><input value={''} style={inputStyle} placeholder="JNL-xxx" /></div>
                <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Description *</label><input value={txForm.description} onChange={e=>setTxForm(f=>({...f,description:e.target.value}))} style={inputStyle} placeholder="Transaction narration" /></div>
                <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Notes</label><input value={txForm.notes} onChange={e=>setTxForm(f=>({...f,notes:e.target.value}))} style={inputStyle} /></div>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
                <button onClick={()=>setShowTxModal(false)} style={btnGhost}>Cancel</button>
                <button onClick={handleAddTx} style={btnPrimary}>Record</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── PAYROLL PANEL ────────────────────────────────────────────
  const PayrollPanel = () => {
    const [staff, setStaff]           = useState<any[]>([]);
    const [allowances, setAllowances] = useState<any[]>([]);
    const [runs, setRuns]             = useState<any[]>([]);
    const [advances, setAdvances]     = useState<any[]>([]);
    const [activeTab, setActiveTab]   = useState<'staff'|'allowances'|'runs'|'advances'>('staff');
    const [loading, setLoading]       = useState(true);
    const [showModal, setShowModal]   = useState(false);
    const [flash, setFlash]           = useState('');
    const [flashType, setFlashType]   = useState<'ok'|'err'>('ok');
    const [form, setForm]             = useState<any>({});
    const [editItem, setEditItem]     = useState<any>(null);

    const showFlash = (msg:string,type:'ok'|'err'='ok') => { setFlash(msg); setFlashType(type); setTimeout(()=>setFlash(''),4000); };

    const fetchAll = useCallback(async () => {
      setLoading(true);
      try {
        const [sr, ar, rr, adv] = await Promise.all([
          fetch(`/api/payroll/staff?year=${year}`, { headers }),
          fetch('/api/payroll/allowance-types', { headers }),
          fetch(`/api/payroll/runs?year=${year}`, { headers }),
          fetch(`/api/payroll/advances?year=${year}`, { headers }),
        ]);
        const [sd, ad, rd, advd] = await Promise.all([sr.json(), ar.json(), rr.json(), adv.json()]);
        setStaff(sd.staff||[]); setAllowances(ad.types||[]); setRuns(rd.runs||[]); setAdvances(advd.advances||[]);
      } catch { showFlash('Failed to load','err'); }
      finally { setLoading(false); }
    },[]);

    useEffect(() => { fetchAll(); },[fetchAll]);

    const STAFF_TYPES = ['LOC','CWC','BOT','VOLUNTEER','CONTRACT','HIRED'];

    const handleSaveStaff = async () => {
      try {
        const url    = editItem ? `/api/payroll/staff/${editItem.id}` : '/api/payroll/staff';
        const method = editItem ? 'PUT' : 'POST';
        const r = await fetch(url, { method, headers, body: JSON.stringify({...form, festivalYear:year}) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash(editItem ? 'Staff updated' : 'Staff added');
        setShowModal(false); setEditItem(null); fetchAll();
      } catch(e:any) { showFlash(e.message,'err'); }
    };

    const handleSaveAllowance = async () => {
      try {
        const url    = editItem ? `/api/payroll/allowance-types/${editItem.id}` : '/api/payroll/allowance-types';
        const method = editItem ? 'PUT' : 'POST';
        const r = await fetch(url, { method, headers, body: JSON.stringify(form) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash(editItem ? 'Updated' : 'Created');
        setShowModal(false); setEditItem(null); fetchAll();
      } catch(e:any) { showFlash(e.message,'err'); }
    };

    const handleDelete = async (endpoint: string) => {
      if (!confirm('Delete?')) return;
      try {
        const r = await fetch(endpoint, { method:'DELETE', headers });
        if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
        showFlash('Deleted'); fetchAll();
      } catch(e:any) { showFlash(e.message,'err'); }
    };

    const handleRetireAdvance = async (id: string) => {
      const amt = prompt('Amount to retire (₦):');
      if (!amt) return;
      try {
        const r = await fetch(`/api/payroll/advances/${id}/retire`, { method:'PUT', headers, body: JSON.stringify({ amountRetired: parseFloat(amt) }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash('Advance retired'); fetchAll();
      } catch(e:any) { showFlash(e.message,'err'); }
    };

    const SUB_TABS = [
      { id:'staff', label:`Staff Register (${staff.length})` },
      { id:'allowances', label:`Allowance Types (${allowances.length})` },
      { id:'runs', label:`Payroll Runs (${runs.length})` },
      { id:'advances', label:`Advances (${advances.length})` },
    ];

    const ADV_STATUS_COLORS: Record<string,string> = { OUTSTANDING:C.red, PARTIALLY_RETIRED:C.orange, RETIRED:'#4ade80', WRITTEN_OFF:C.muted };

    return (
      <div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:10 }}>
          <h2 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, fontSize:18, margin:0 }}>👥 Payroll & Staff Management</h2>
          <button onClick={()=>{ setEditItem(null); setForm({}); setShowModal(true); }} style={btnPrimary}>
            + Add {activeTab === 'staff' ? 'Staff' : activeTab === 'allowances' ? 'Allowance Type' : activeTab === 'advances' ? 'Advance' : 'Payroll Run'}
          </button>
        </div>

        {flash && <div style={{ padding:'10px 16px', borderRadius:8, marginBottom:12, fontSize:13, background:flashType==='ok'?'rgba(74,222,128,0.1)':'rgba(239,68,68,0.1)', border:`1px solid ${flashType==='ok'?'#4ade80':C.red}`, color:flashType==='ok'?'#4ade80':C.red }}>{flash}</div>}

        {/* Sub tabs */}
        <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:`1px solid ${C.border}`, flexWrap:'wrap' }}>
          {SUB_TABS.map(t => (
            <button key={t.id} onClick={()=>setActiveTab(t.id as any)} style={{ padding:'10px 16px', background:'none', border:'none', borderBottom:`2px solid ${activeTab===t.id?C.gold:'transparent'}`, color:activeTab===t.id?C.goldLt:C.muted, fontSize:13, fontWeight:activeTab===t.id?700:400, cursor:'pointer' }}>{t.label}</button>
          ))}
        </div>

        {loading ? <div style={{ textAlign:'center', padding:40, color:C.muted }}>Loading...</div> : (
          <>
            {/* Staff Register */}
            {activeTab === 'staff' && (
              staff.length === 0 ? <div style={{ textAlign:'center', padding:60, color:C.muted }}>No staff registered</div> :
              <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead><tr style={{ background:C.surface }}>{['Name','Type','Role','Phone','Bank','Status','Actions'].map(h=><th key={h} style={{ padding:'8px 12px', textAlign:'left', color:C.muted, fontSize:11, borderBottom:`1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {staff.map((s,i)=>(
                      <tr key={s.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                        <td style={{ padding:'8px 12px', color:C.text, fontWeight:600 }}>{s.firstName} {s.lastName}</td>
                        <td style={{ padding:'8px 12px' }}><span style={{ background:C.surface, borderRadius:10, padding:'2px 8px', fontSize:11, color:C.blue }}>{s.staffType}</span></td>
                        <td style={{ padding:'8px 12px', color:C.muted, fontSize:12 }}>{s.role||'—'}</td>
                        <td style={{ padding:'8px 12px', color:C.muted, fontSize:12 }}>{s.phone||'—'}</td>
                        <td style={{ padding:'8px 12px', color:C.muted, fontSize:11 }}>{s.bankName ? `${s.bankName}...${(s.accountNumber||'').slice(-4)}` : '—'}</td>
                        <td style={{ padding:'8px 12px', color:s.isActive?'#4ade80':C.red, fontSize:11 }}>{s.isActive?'Active':'Inactive'}</td>
                        <td style={{ padding:'8px 12px' }}>
                          <div style={{ display:'flex', gap:4 }}>
                            <button onClick={()=>{ setEditItem(s); setForm({firstName:s.firstName,lastName:s.lastName,email:s.email||'',phone:s.phone||'',staffType:s.staffType,role:s.role||'',village:s.village||'',bankName:s.bankName||'',accountNumber:s.accountNumber||'',accountName:s.accountName||'',taxId:s.taxId||'',notes:s.notes||''}); setShowModal(true); }} style={{...btnGhost,padding:'4px 8px',fontSize:11}}>✏️</button>
                            <button onClick={()=>handleDelete(`/api/payroll/staff/${s.id}`)} style={{...btnDanger,padding:'4px 8px'}}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Allowance Types */}
            {activeTab === 'allowances' && (
              allowances.length === 0 ? <div style={{ textAlign:'center', padding:60, color:C.muted }}>No allowance types defined</div> :
              <div style={{ display:'grid', gap:10 }}>
                {allowances.map(a => (
                  <div key={a.id} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 16px', display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ color:C.goldLt, fontWeight:600 }}>{a.name}</div>
                      <div style={{ color:C.muted, fontSize:12 }}>{a.description||'No description'}</div>
                    </div>
                    <div style={{ color:C.teal, fontWeight:700, fontSize:16 }}>₦{a.amount.toLocaleString()}</div>
                    {a.taxable && <span style={{ background:C.red+'22', color:C.red, borderRadius:10, padding:'2px 8px', fontSize:11 }}>Taxable</span>}
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={()=>{ setEditItem(a); setForm({name:a.name,description:a.description||'',amount:String(a.amount),taxable:a.taxable}); setShowModal(true); }} style={{...btnGhost,padding:'4px 8px',fontSize:11}}>✏️</button>
                      <button onClick={()=>handleDelete(`/api/payroll/allowance-types/${a.id}`)} style={{...btnDanger,padding:'4px 8px'}}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Payroll Runs */}
            {activeTab === 'runs' && (
              runs.length === 0 ? <div style={{ textAlign:'center', padding:60, color:C.muted }}>No payroll runs yet</div> :
              <div style={{ display:'grid', gap:12 }}>
                {runs.map(r => (
                  <div key={r.id} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'16px 18px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                      <div>
                        <div style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, fontSize:14 }}>{r.title}</div>
                        <div style={{ color:C.muted, fontSize:12 }}>{r.period} — {r.entries?.length||0} staff entries</div>
                      </div>
                      <span style={{ background:C.blue+'22', color:C.blue, borderRadius:12, padding:'3px 12px', fontSize:11, fontWeight:700 }}>{r.status}</span>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:12 }}>
                      {[['Gross',r.totalGross,C.text],['Tax',r.totalTax,C.red],['Net',r.totalNet,'#4ade80']].map(([l,v,c])=>(
                        <div key={String(l)} style={{ background:C.surface, borderRadius:8, padding:'8px 12px', textAlign:'center' }}>
                          <div style={{ fontSize:14, fontWeight:700, color:String(c) }}>₦{(Number(v)||0).toLocaleString()}</div>
                          <div style={{ fontSize:11, color:C.muted }}>{l}</div>
                        </div>
                      ))}
                    </div>
                    {r.status === 'DRAFT' && (
                      <div style={{ display:'flex', gap:8 }}>
                        <button onClick={async()=>{ const res=await fetch(`/api/payroll/runs/${r.id}/status`,{method:'PUT',headers,body:JSON.stringify({status:'PROCESSED'})}); if(res.ok){showFlash('Processed');fetchAll();} }} style={{...btnPrimary,padding:'6px 14px',fontSize:12}}>✓ Process</button>
                        <button onClick={()=>handleDelete(`/api/payroll/runs/${r.id}`)} style={{...btnDanger,padding:'6px 12px'}}>🗑</button>
                      </div>
                    )}
                    {r.status === 'PROCESSED' && (
                      <button onClick={async()=>{ const res=await fetch(`/api/payroll/runs/${r.id}/status`,{method:'PUT',headers,body:JSON.stringify({status:'PAID'})}); if(res.ok){showFlash('Marked as Paid');fetchAll();} }} style={{...btnPrimary,padding:'6px 14px',fontSize:12}}>💰 Mark as Paid</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Advances */}
            {activeTab === 'advances' && (
              advances.length === 0 ? <div style={{ textAlign:'center', padding:60, color:C.muted }}>No advances recorded</div> :
              <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead><tr style={{ background:C.surface }}>{['Staff','Purpose','Amount','Retired','Balance','Status','Actions'].map(h=><th key={h} style={{ padding:'8px 12px', textAlign:'left', color:C.muted, fontSize:11, borderBottom:`1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {advances.map(a=>(
                      <tr key={a.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                        <td style={{ padding:'8px 12px', color:C.text }}>{a.staffMember?.firstName} {a.staffMember?.lastName}</td>
                        <td style={{ padding:'8px 12px', color:C.muted, fontSize:12 }}>{a.purpose}</td>
                        <td style={{ padding:'8px 12px', color:C.text, textAlign:'right' }}>₦{a.amount.toLocaleString()}</td>
                        <td style={{ padding:'8px 12px', color:'#4ade80', textAlign:'right' }}>₦{a.amountRetired.toLocaleString()}</td>
                        <td style={{ padding:'8px 12px', color:a.balance>0?C.red:'#4ade80', textAlign:'right', fontWeight:700 }}>₦{a.balance.toLocaleString()}</td>
                        <td style={{ padding:'8px 12px' }}><span style={{ background:ADV_STATUS_COLORS[a.status]+'22', color:ADV_STATUS_COLORS[a.status], borderRadius:10, padding:'2px 8px', fontSize:10, fontWeight:700 }}>{a.status}</span></td>
                        <td style={{ padding:'8px 12px' }}>
                          {!['RETIRED','WRITTEN_OFF'].includes(a.status) && <button onClick={()=>handleRetireAdvance(a.id)} style={{...btnPrimary,padding:'4px 10px',fontSize:11}}>Retire</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Modal — Staff */}
        {showModal && activeTab === 'staff' && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:28, width:'100%', maxWidth:620, maxHeight:'90vh', overflowY:'auto' }}>
              <h3 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, margin:'0 0 20px' }}>{editItem?'✏️ Edit Staff':'👤 Add Staff Member'}</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <div><label style={labelStyle}>First Name *</label><input value={form.firstName||''} onChange={e=>setForm((f:any)=>({...f,firstName:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Last Name *</label><input value={form.lastName||''} onChange={e=>setForm((f:any)=>({...f,lastName:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Staff Type *</label><select value={form.staffType||'LOC'} onChange={e=>setForm((f:any)=>({...f,staffType:e.target.value}))} style={inputStyle}>{STAFF_TYPES.map(t=><option key={t} value={t}>{t}</option>)}</select></div>
                <div><label style={labelStyle}>Role / Title</label><input value={form.role||''} onChange={e=>setForm((f:any)=>({...f,role:e.target.value}))} style={inputStyle} placeholder="Chairman, Member, etc." /></div>
                <div><label style={labelStyle}>Email</label><input value={form.email||''} onChange={e=>setForm((f:any)=>({...f,email:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Phone</label><input value={form.phone||''} onChange={e=>setForm((f:any)=>({...f,phone:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Village</label><input value={form.village||''} onChange={e=>setForm((f:any)=>({...f,village:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Tax ID (NIN/TIN)</label><input value={form.taxId||''} onChange={e=>setForm((f:any)=>({...f,taxId:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Bank Name</label><input value={form.bankName||''} onChange={e=>setForm((f:any)=>({...f,bankName:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Account Number</label><input value={form.accountNumber||''} onChange={e=>setForm((f:any)=>({...f,accountNumber:e.target.value}))} style={inputStyle} /></div>
                <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Account Name</label><input value={form.accountName||''} onChange={e=>setForm((f:any)=>({...f,accountName:e.target.value}))} style={inputStyle} /></div>
                <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Notes</label><textarea value={form.notes||''} onChange={e=>setForm((f:any)=>({...f,notes:e.target.value}))} rows={2} style={{...inputStyle,resize:'vertical'}} /></div>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
                <button onClick={()=>{setShowModal(false);setEditItem(null);}} style={btnGhost}>Cancel</button>
                <button onClick={handleSaveStaff} style={btnPrimary}>{editItem?'Update':'Add Staff'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal — Allowance Type */}
        {showModal && activeTab === 'allowances' && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:28, width:'100%', maxWidth:460 }}>
              <h3 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, margin:'0 0 20px' }}>{editItem?'✏️ Edit Allowance':'➕ New Allowance Type'}</h3>
              <div style={{ display:'grid', gap:14 }}>
                <div><label style={labelStyle}>Name *</label><input value={form.name||''} onChange={e=>setForm((f:any)=>({...f,name:e.target.value}))} style={inputStyle} placeholder="e.g. Sitting Allowance" /></div>
                <div><label style={labelStyle}>Amount (₦) *</label><input type="number" value={form.amount||''} onChange={e=>setForm((f:any)=>({...f,amount:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Description</label><input value={form.description||''} onChange={e=>setForm((f:any)=>({...f,description:e.target.value}))} style={inputStyle} /></div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}><input type="checkbox" checked={form.taxable||false} onChange={e=>setForm((f:any)=>({...f,taxable:e.target.checked}))} /><span style={{ color:C.text, fontSize:13 }}>Taxable (subject to PAYE)</span></div>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
                <button onClick={()=>{setShowModal(false);setEditItem(null);}} style={btnGhost}>Cancel</button>
                <button onClick={handleSaveAllowance} style={btnPrimary}>{editItem?'Update':'Create'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal — Add Advance */}
        {showModal && activeTab === 'advances' && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:28, width:'100%', maxWidth:500 }}>
              <h3 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, margin:'0 0 20px' }}>💵 Issue Staff Advance</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Staff Member *</label><select value={form.staffMemberId||''} onChange={e=>setForm((f:any)=>({...f,staffMemberId:e.target.value}))} style={inputStyle}><option value="">-- Select staff --</option>{staff.map(s=><option key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.staffType})</option>)}</select></div>
                <div><label style={labelStyle}>Amount (₦) *</label><input type="number" value={form.amount||''} onChange={e=>setForm((f:any)=>({...f,amount:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Date Issued *</label><input type="date" value={form.dateIssued||new Date().toISOString().split('T')[0]} onChange={e=>setForm((f:any)=>({...f,dateIssued:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Due Date</label><input type="date" value={form.dueDate||''} onChange={e=>setForm((f:any)=>({...f,dueDate:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Approved By</label><input value={form.approvedBy||''} onChange={e=>setForm((f:any)=>({...f,approvedBy:e.target.value}))} style={inputStyle} /></div>
                <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Purpose *</label><input value={form.purpose||''} onChange={e=>setForm((f:any)=>({...f,purpose:e.target.value}))} style={inputStyle} placeholder="Purpose of advance" /></div>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
                <button onClick={()=>{setShowModal(false);setEditItem(null);}} style={btnGhost}>Cancel</button>
                <button onClick={async()=>{ try { const r=await fetch('/api/payroll/advances',{method:'POST',headers,body:JSON.stringify({...form,festivalYear:year})}); const d=await r.json(); if(!r.ok)throw new Error(d.error); showFlash('Advance issued'); setShowModal(false); fetchAll(); } catch(e:any){showFlash(e.message,'err');} }} style={btnPrimary}>Issue Advance</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── TAX PANEL ────────────────────────────────────────────────
  const TaxPanel = () => {
    const [records, setRecords] = useState<any[]>([]);
    const [totals, setTotals]   = useState<any>({});
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [flash, setFlash]     = useState('');
    const [flashType, setFlashType] = useState<'ok'|'err'>('ok');
    const [filterType, setFilterType] = useState('');
    const [form, setForm] = useState({ type:'WHT', vendorName:'', vendorTaxId:'', transactionRef:'', grossAmount:'', taxRate:'5', transactionDate:new Date().toISOString().split('T')[0], dueDate:'', notes:'' });

    const showFlash = (msg:string,type:'ok'|'err'='ok') => { setFlash(msg); setFlashType(type); setTimeout(()=>setFlash(''),4000); };

    const fetchRecords = useCallback(async () => {
      setLoading(true);
      try {
        let url = `/api/finance/tax?year=${year}`;
        if (filterType) url += `&type=${filterType}`;
        const r = await fetch(url, { headers });
        const d = await r.json();
        setRecords(d.records||[]); setTotals(d.totals||{});
      } catch { showFlash('Failed to load','err'); }
      finally { setLoading(false); }
    },[filterType]);

    useEffect(() => { fetchRecords(); },[fetchRecords]);

    const handleSave = async () => {
      try {
        const r = await fetch('/api/finance/tax', { method:'POST', headers, body: JSON.stringify({...form, festivalYear:year}) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash('Tax record created'); setShowModal(false); fetchRecords();
      } catch(e:any) { showFlash(e.message,'err'); }
    };

    const handleRemit = async (id:string) => {
      const ref = prompt('Enter remittance receipt reference:');
      if (!ref) return;
      try {
        const r = await fetch(`/api/finance/tax/${id}/remit`, { method:'PUT', headers, body: JSON.stringify({ receiptRef: ref }) });
        if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
        showFlash('Marked as remitted'); fetchRecords();
      } catch(e:any) { showFlash(e.message,'err'); }
    };

    const TAX_TYPES = ['WHT','PAYE','VAT','OTHER'];
    const WHT_RATES = [{'label':'5% — Services','value':'5'},{'label':'10% — Rent/Dividend','value':'10'},{'label':'2.5% — Construction','value':'2.5'}];
    const STATUS_COLORS: Record<string,string> = { ACCRUED:C.orange, REMITTED:'#4ade80', EXEMPT:C.muted };

    const computedTax = form.grossAmount && form.taxRate ? parseFloat(form.grossAmount) * (parseFloat(form.taxRate)/100) : 0;

    return (
      <div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:10 }}>
          <div>
            <h2 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, fontSize:18, margin:0 }}>⚖️ Tax Records — CAMA 2020</h2>
            <p style={{ color:C.muted, fontSize:12, margin:'4px 0 0' }}>Non-profit tax obligations: WHT, PAYE — Calendar Year {year}</p>
          </div>
          <button onClick={()=>setShowModal(true)} style={btnPrimary}>+ Record Tax</button>
        </div>

        {flash && <div style={{ padding:'10px 16px', borderRadius:8, marginBottom:12, fontSize:13, background:flashType==='ok'?'rgba(74,222,128,0.1)':'rgba(239,68,68,0.1)', border:`1px solid ${flashType==='ok'?'#4ade80':C.red}`, color:flashType==='ok'?'#4ade80':C.red }}>{flash}</div>}

        {/* CAMA 2020 Note */}
        <div style={{ background:'rgba(201,168,76,0.08)', border:`1px solid ${C.gold}44`, borderRadius:10, padding:'12px 16px', marginBottom:20, fontSize:12, color:C.muted }}>
          <strong style={{ color:C.gold }}>CAMA 2020 Non-Profit Tax Rules: </strong>
          WHT deductible at source on payments to vendors/contractors (5–10%). PAYE applicable to any staff earning above threshold. ERUE Festival as a cultural organisation is generally exempt from corporate income tax but must deduct and remit WHT.
        </div>

        {/* Summary */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:20 }}>
          {[
            { label:'Total Tax Accrued', value:`₦${(totals.totalAccrued||0).toLocaleString()}`, color:C.orange },
            { label:'Total Remitted', value:`₦${(totals.totalRemitted||0).toLocaleString()}`, color:'#4ade80' },
            { label:'Outstanding (Payable)', value:`₦${((totals.totalAccrued||0)-(totals.totalRemitted||0)).toLocaleString()}`, color:C.red },
          ].map(s=>(
            <div key={s.label} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px', textAlign:'center' }}>
              <div style={{ fontSize:18, fontWeight:700, color:s.color }}>{s.value}</div>
              <div style={{ fontSize:12, color:C.muted, marginTop:4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display:'flex', gap:10, marginBottom:16 }}>
          <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{...inputStyle,maxWidth:180}}>
            <option value="">All Types</option>
            {TAX_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          <button onClick={fetchRecords} style={btnGhost}>🔄</button>
        </div>

        {loading ? <div style={{ textAlign:'center', padding:40, color:C.muted }}>Loading...</div> :
          records.length === 0 ? <div style={{ textAlign:'center', padding:60, color:C.muted }}>No tax records</div> : (
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ background:C.surface }}>{['Date','Type','Vendor','Gross','Rate','Tax Amount','Status','Actions'].map(h=><th key={h} style={{ padding:'8px 12px', textAlign:['Gross','Tax Amount'].includes(h)?'right':'left', color:C.muted, fontSize:11, borderBottom:`1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
              <tbody>
                {records.map(r=>(
                  <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                    <td style={{ padding:'8px 12px', color:C.muted, fontSize:12 }}>{new Date(r.transactionDate).toLocaleDateString()}</td>
                    <td style={{ padding:'8px 12px', color:C.blue, fontWeight:700 }}>{r.type}</td>
                    <td style={{ padding:'8px 12px', color:C.text, fontSize:12 }}>{r.vendorName||'—'}</td>
                    <td style={{ padding:'8px 12px', color:C.text, textAlign:'right' }}>₦{r.grossAmount.toLocaleString()}</td>
                    <td style={{ padding:'8px 12px', color:C.muted, textAlign:'center' }}>{r.taxRate}%</td>
                    <td style={{ padding:'8px 12px', color:C.orange, textAlign:'right', fontWeight:700 }}>₦{r.taxAmount.toLocaleString()}</td>
                    <td style={{ padding:'8px 12px' }}><span style={{ background:STATUS_COLORS[r.status]+'22', color:STATUS_COLORS[r.status], borderRadius:10, padding:'2px 8px', fontSize:10, fontWeight:700 }}>{r.status}</span></td>
                    <td style={{ padding:'8px 12px' }}>
                      {r.status === 'ACCRUED' && <button onClick={()=>handleRemit(r.id)} style={{...btnPrimary,padding:'4px 10px',fontSize:11}}>✓ Remit</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:28, width:'100%', maxWidth:500 }}>
              <h3 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, margin:'0 0 20px' }}>⚖️ Record Tax Obligation</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <div><label style={labelStyle}>Tax Type *</label><select value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))} style={inputStyle}>{TAX_TYPES.map(t=><option key={t} value={t}>{t}</option>)}</select></div>
                <div><label style={labelStyle}>Tax Rate *</label><select value={form.taxRate} onChange={e=>setForm(f=>({...f,taxRate:e.target.value}))} style={inputStyle}>{WHT_RATES.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}</select></div>
                <div><label style={labelStyle}>Vendor / Payer</label><input value={form.vendorName} onChange={e=>setForm(f=>({...f,vendorName:e.target.value}))} style={inputStyle} placeholder="Name" /></div>
                <div><label style={labelStyle}>Vendor Tax ID</label><input value={form.vendorTaxId} onChange={e=>setForm(f=>({...f,vendorTaxId:e.target.value}))} style={inputStyle} placeholder="TIN/RC" /></div>
                <div><label style={labelStyle}>Gross Amount (₦) *</label><input type="number" value={form.grossAmount} onChange={e=>setForm(f=>({...f,grossAmount:e.target.value}))} style={inputStyle} /></div>
                <div style={{ display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
                  <label style={labelStyle}>Computed Tax</label>
                  <div style={{ padding:'8px 12px', borderRadius:6, background:C.surface, color:C.orange, fontWeight:700, fontSize:15 }}>₦{computedTax.toLocaleString()}</div>
                </div>
                <div><label style={labelStyle}>Transaction Date *</label><input type="date" value={form.transactionDate} onChange={e=>setForm(f=>({...f,transactionDate:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Due Date</label><input type="date" value={form.dueDate} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Transaction Ref</label><input value={form.transactionRef} onChange={e=>setForm(f=>({...f,transactionRef:e.target.value}))} style={inputStyle} /></div>
                <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Notes</label><input value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={inputStyle} /></div>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
                <button onClick={()=>setShowModal(false)} style={btnGhost}>Cancel</button>
                <button onClick={handleSave} style={btnPrimary}>Record Tax</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── INVENTORY PANEL ──────────────────────────────────────────
  const InventoryPanel = () => {
    const [items, setItems]     = useState<any[]>([]);
    const [assets, setAssets]   = useState<any[]>([]);
    const [view, setView]       = useState<'inventory'|'assets'>('inventory');
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showTxModal, setShowTxModal] = useState(false);
    const [selItem, setSelItem] = useState<any>(null);
    const [editItem, setEditItem] = useState<any>(null);
    const [flash, setFlash]     = useState('');
    const [flashType, setFlashType] = useState<'ok'|'err'>('ok');
    const [form, setForm]       = useState<any>({});
    const [txForm, setTxForm]   = useState({ type:'STOCK_IN', quantity:'', unitCost:'', reference:'', notes:'' });

    const showFlash = (msg:string,type:'ok'|'err'='ok') => { setFlash(msg); setFlashType(type); setTimeout(()=>setFlash(''),4000); };

    const fetchAll = useCallback(async () => {
      setLoading(true);
      try {
        const [ir, ar] = await Promise.all([
          fetch('/api/finance/inventory', { headers }),
          fetch('/api/finance/fixed-assets', { headers }),
        ]);
        const [id, ad] = await Promise.all([ir.json(), ar.json()]);
        setItems(id.items||[]); setAssets(ad.assets||[]);
      } catch { showFlash('Failed to load','err'); }
      finally { setLoading(false); }
    },[]);

    useEffect(() => { fetchAll(); },[fetchAll]);

    const handleSaveItem = async () => {
      try {
        const url = editItem ? `/api/finance/inventory/${editItem.id}` : '/api/finance/inventory';
        const method = editItem ? 'PUT' : 'POST';
        const r = await fetch(url, { method, headers, body: JSON.stringify({...form, festivalYear:year}) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash(editItem?'Updated':'Created'); setShowModal(false); setEditItem(null); fetchAll();
      } catch(e:any) { showFlash(e.message,'err'); }
    };

    const handleSaveAsset = async () => {
      try {
        const url = editItem ? `/api/finance/fixed-assets/${editItem.id}` : '/api/finance/fixed-assets';
        const method = editItem ? 'PUT' : 'POST';
        const r = await fetch(url, { method, headers, body: JSON.stringify({...form, festivalYear:year}) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash(editItem?'Updated':'Created'); setShowModal(false); setEditItem(null); fetchAll();
      } catch(e:any) { showFlash(e.message,'err'); }
    };

    const handleStockTx = async () => {
      if (!selItem) return;
      try {
        const r = await fetch(`/api/finance/inventory/${selItem.id}/transaction`, { method:'POST', headers, body: JSON.stringify(txForm) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash('Transaction recorded'); setShowTxModal(false); fetchAll();
      } catch(e:any) { showFlash(e.message,'err'); }
    };

    const handleDepreciate = async (id:string) => {
      try {
        const r = await fetch(`/api/finance/fixed-assets/${id}/depreciate`, { method:'POST', headers });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        showFlash(`Depreciated — Book Value: ₦${d.currentBookValue?.toLocaleString()}`); fetchAll();
      } catch(e:any) { showFlash(e.message,'err'); }
    };

    const totalInventoryValue = items.reduce((s,i)=>s+(i.currentStock*i.unitCost),0);
    const totalAssetValue     = assets.reduce((s,a)=>s+a.currentBookValue,0);
    const ASSET_CATEGORIES = ['EQUIPMENT','FURNITURE','VEHICLE','TECHNOLOGY','OTHER'];
    const CONDITIONS       = ['EXCELLENT','GOOD','FAIR','POOR'];

    return (
      <div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:10 }}>
          <h2 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, fontSize:18, margin:0 }}>📦 Inventory & Fixed Assets</h2>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={()=>setView('inventory')} style={{...btnGhost, background:view==='inventory'?C.gold:'none', color:view==='inventory'?'#000':C.text}}>📦 Inventory</button>
            <button onClick={()=>setView('assets')} style={{...btnGhost, background:view==='assets'?C.gold:'none', color:view==='assets'?'#000':C.text}}>🏢 Fixed Assets</button>
            <button onClick={()=>{ setEditItem(null); setForm({}); setShowModal(true); }} style={btnPrimary}>+ Add {view==='inventory'?'Item':'Asset'}</button>
          </div>
        </div>

        {flash && <div style={{ padding:'10px 16px', borderRadius:8, marginBottom:12, fontSize:13, background:flashType==='ok'?'rgba(74,222,128,0.1)':'rgba(239,68,68,0.1)', border:`1px solid ${flashType==='ok'?'#4ade80':C.red}`, color:flashType==='ok'?'#4ade80':C.red }}>{flash}</div>}

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
          {[
            { label:'Inventory Value', value:`₦${totalInventoryValue.toLocaleString()}`, color:C.teal, emoji:'📦' },
            { label:'Fixed Assets Value', value:`₦${totalAssetValue.toLocaleString()}`, color:C.blue, emoji:'🏢' },
          ].map(s=>(
            <div key={s.label} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px', textAlign:'center' }}>
              <div style={{ fontSize:24 }}>{s.emoji}</div>
              <div style={{ fontSize:18, fontWeight:700, color:s.color }}>{s.value}</div>
              <div style={{ fontSize:12, color:C.muted }}>{s.label}</div>
            </div>
          ))}
        </div>

        {loading ? <div style={{ textAlign:'center', padding:40, color:C.muted }}>Loading...</div> : (
          view === 'inventory' ? (
            items.length === 0 ? <div style={{ textAlign:'center', padding:60, color:C.muted }}>No inventory items</div> : (
              <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead><tr style={{ background:C.surface }}>{['Item','Category','Stock','Unit Cost','Value','Reorder','Actions'].map(h=><th key={h} style={{ padding:'8px 12px', textAlign:['Stock','Unit Cost','Value'].includes(h)?'right':'left', color:C.muted, fontSize:11, borderBottom:`1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {items.map(i=>{
                      const isLow = i.currentStock <= i.reorderLevel;
                      return (
                        <tr key={i.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                          <td style={{ padding:'8px 12px', color:C.text, fontWeight:600 }}>{i.name}{isLow&&<span style={{ marginLeft:6, color:C.red, fontSize:10 }}>⚠️LOW</span>}</td>
                          <td style={{ padding:'8px 12px', color:C.muted, fontSize:12 }}>{i.category||'—'}</td>
                          <td style={{ padding:'8px 12px', color:isLow?C.red:C.text, textAlign:'right', fontWeight:700 }}>{i.currentStock} {i.unit}</td>
                          <td style={{ padding:'8px 12px', color:C.muted, textAlign:'right' }}>₦{i.unitCost.toLocaleString()}</td>
                          <td style={{ padding:'8px 12px', color:C.teal, textAlign:'right', fontWeight:600 }}>₦{(i.currentStock*i.unitCost).toLocaleString()}</td>
                          <td style={{ padding:'8px 12px', color:C.muted, textAlign:'right', fontSize:12 }}>{i.reorderLevel} {i.unit}</td>
                          <td style={{ padding:'8px 12px' }}>
                            <div style={{ display:'flex', gap:4 }}>
                              <button onClick={()=>{setSelItem(i);setTxForm({type:'STOCK_IN',quantity:'',unitCost:String(i.unitCost),reference:'',notes:''});setShowTxModal(true);}} style={{...btnPrimary,padding:'4px 8px',fontSize:11}}>+/-</button>
                              <button onClick={()=>{setEditItem(i);setForm({name:i.name,description:i.description||'',category:i.category||'',sku:i.sku||'',unit:i.unit,unitCost:String(i.unitCost),sellingPrice:String(i.sellingPrice),reorderLevel:String(i.reorderLevel),notes:i.notes||''});setShowModal(true);}} style={{...btnGhost,padding:'4px 8px',fontSize:11}}>✏️</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            assets.length === 0 ? <div style={{ textAlign:'center', padding:60, color:C.muted }}>No fixed assets registered</div> : (
              <div style={{ display:'grid', gap:12 }}>
                {assets.map(a=>(
                  <div key={a.id} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'16px 18px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                      <div>
                        <div style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, fontSize:14 }}>{a.name}</div>
                        <div style={{ color:C.muted, fontSize:12 }}>{a.category} — {a.location||'No location'} — S/N: {a.serialNumber||'N/A'}</div>
                      </div>
                      <span style={{ background:C.blue+'22', color:C.blue, borderRadius:12, padding:'3px 12px', fontSize:11 }}>{a.condition}</span>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
                      {[['Cost',`₦${a.purchaseCost.toLocaleString()}`,C.text],['Book Value',`₦${a.currentBookValue.toLocaleString()}`,C.blue],['Life',`${a.usefulLifeYears} yrs`,C.muted]].map(([l,v,c])=>(
                        <div key={String(l)} style={{ background:C.surface, borderRadius:8, padding:'8px', textAlign:'center' }}>
                          <div style={{ fontSize:13, fontWeight:700, color:String(c) }}>{v}</div>
                          <div style={{ fontSize:11, color:C.muted }}>{l}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display:'flex', gap:8, marginTop:12 }}>
                      <button onClick={()=>handleDepreciate(a.id)} style={{...btnGhost,padding:'5px 12px',fontSize:12}}>📉 Depreciate</button>
                      <button onClick={()=>{setEditItem(a);setForm({name:a.name,description:a.description||'',category:a.category,serialNumber:a.serialNumber||'',location:a.location||'',condition:a.condition,currentBookValue:String(a.currentBookValue),notes:a.notes||''});setShowModal(true);}} style={{...btnGhost,padding:'5px 12px',fontSize:12}}>✏️ Edit</button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )
        )}

        {/* Add Inventory Item Modal */}
        {showModal && view === 'inventory' && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:28, width:'100%', maxWidth:560 }}>
              <h3 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, margin:'0 0 20px' }}>{editItem?'✏️ Edit Item':'📦 Add Inventory Item'}</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Name *</label><input value={form.name||''} onChange={e=>setForm((f:any)=>({...f,name:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Category</label><input value={form.category||''} onChange={e=>setForm((f:any)=>({...f,category:e.target.value}))} style={inputStyle} placeholder="e.g. Merchandise" /></div>
                <div><label style={labelStyle}>Unit</label><input value={form.unit||'unit'} onChange={e=>setForm((f:any)=>({...f,unit:e.target.value}))} style={inputStyle} /></div>
                {!editItem && <div><label style={labelStyle}>Opening Stock</label><input type="number" value={form.openingStock||'0'} onChange={e=>setForm((f:any)=>({...f,openingStock:e.target.value}))} style={inputStyle} /></div>}
                <div><label style={labelStyle}>Unit Cost (₦)</label><input type="number" value={form.unitCost||'0'} onChange={e=>setForm((f:any)=>({...f,unitCost:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Selling Price (₦)</label><input type="number" value={form.sellingPrice||'0'} onChange={e=>setForm((f:any)=>({...f,sellingPrice:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Reorder Level</label><input type="number" value={form.reorderLevel||'0'} onChange={e=>setForm((f:any)=>({...f,reorderLevel:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>SKU</label><input value={form.sku||''} onChange={e=>setForm((f:any)=>({...f,sku:e.target.value}))} style={inputStyle} /></div>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
                <button onClick={()=>{setShowModal(false);setEditItem(null);}} style={btnGhost}>Cancel</button>
                <button onClick={handleSaveItem} style={btnPrimary}>{editItem?'Update':'Add Item'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Add Asset Modal */}
        {showModal && view === 'assets' && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:28, width:'100%', maxWidth:580 }}>
              <h3 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, margin:'0 0 20px' }}>{editItem?'✏️ Edit Asset':'🏢 Register Fixed Asset'}</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Asset Name *</label><input value={form.name||''} onChange={e=>setForm((f:any)=>({...f,name:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Category *</label><select value={form.category||'EQUIPMENT'} onChange={e=>setForm((f:any)=>({...f,category:e.target.value}))} style={inputStyle}>{ASSET_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
                <div><label style={labelStyle}>Condition</label><select value={form.condition||'GOOD'} onChange={e=>setForm((f:any)=>({...f,condition:e.target.value}))} style={inputStyle}>{CONDITIONS.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
                {!editItem && <>
                  <div><label style={labelStyle}>Purchase Date *</label><input type="date" value={form.purchaseDate||''} onChange={e=>setForm((f:any)=>({...f,purchaseDate:e.target.value}))} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Purchase Cost (₦) *</label><input type="number" value={form.purchaseCost||''} onChange={e=>setForm((f:any)=>({...f,purchaseCost:e.target.value}))} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Residual Value (₦)</label><input type="number" value={form.residualValue||'0'} onChange={e=>setForm((f:any)=>({...f,residualValue:e.target.value}))} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Useful Life (years)</label><input type="number" value={form.usefulLifeYears||'3'} onChange={e=>setForm((f:any)=>({...f,usefulLifeYears:e.target.value}))} style={inputStyle} /></div>
                </>}
                {editItem && <div><label style={labelStyle}>Current Book Value (₦)</label><input type="number" value={form.currentBookValue||''} onChange={e=>setForm((f:any)=>({...f,currentBookValue:e.target.value}))} style={inputStyle} /></div>}
                <div><label style={labelStyle}>Serial Number</label><input value={form.serialNumber||''} onChange={e=>setForm((f:any)=>({...f,serialNumber:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Location</label><input value={form.location||''} onChange={e=>setForm((f:any)=>({...f,location:e.target.value}))} style={inputStyle} /></div>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
                <button onClick={()=>{setShowModal(false);setEditItem(null);}} style={btnGhost}>Cancel</button>
                <button onClick={handleSaveAsset} style={btnPrimary}>{editItem?'Update':'Register Asset'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Stock Transaction Modal */}
        {showTxModal && selItem && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:28, width:'100%', maxWidth:440 }}>
              <h3 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, margin:'0 0 6px' }}>Stock Movement — {selItem.name}</h3>
              <p style={{ color:C.muted, fontSize:13, margin:'0 0 20px' }}>Current stock: <strong style={{ color:C.teal }}>{selItem.currentStock} {selItem.unit}</strong></p>
              <div style={{ display:'grid', gap:14 }}>
                <div><label style={labelStyle}>Movement Type *</label><select value={txForm.type} onChange={e=>setTxForm(f=>({...f,type:e.target.value}))} style={inputStyle}>{['STOCK_IN','STOCK_OUT','SALE','ADJUSTMENT','RETURN'].map(t=><option key={t} value={t}>{t}</option>)}</select></div>
                <div><label style={labelStyle}>Quantity *</label><input type="number" value={txForm.quantity} onChange={e=>setTxForm(f=>({...f,quantity:e.target.value}))} style={inputStyle} placeholder="0" /></div>
                <div><label style={labelStyle}>Unit Cost (₦)</label><input type="number" value={txForm.unitCost} onChange={e=>setTxForm(f=>({...f,unitCost:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Reference</label><input value={txForm.reference} onChange={e=>setTxForm(f=>({...f,reference:e.target.value}))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Notes</label><input value={txForm.notes} onChange={e=>setTxForm(f=>({...f,notes:e.target.value}))} style={inputStyle} /></div>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
                <button onClick={()=>setShowTxModal(false)} style={btnGhost}>Cancel</button>
                <button onClick={handleStockTx} style={btnPrimary}>Record Movement</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── REPORTS PANEL ────────────────────────────────────────────
  const ReportsPanel = () => {
    const [summary, setSummary]   = useState<any>(null);
    const [payroll, setPayroll]   = useState<any>(null);
    const [taxSched, setTaxSched] = useState<any>(null);
    const [ops, setOps]           = useState<any>(null);
    const [loading, setLoading]   = useState(true);
    const [activeRpt, setActiveRpt] = useState<'financial'|'payroll'|'tax'|'ops'>('financial');

    const fetchAll = useCallback(async () => {
      setLoading(true);
      try {
        const [sr, pr, tr, or_] = await Promise.all([
          fetch(`/api/reports/financial-summary?year=${year}`, { headers }),
          fetch(`/api/reports/payroll-summary?year=${year}`, { headers }),
          fetch(`/api/reports/tax-schedule?year=${year}`, { headers }),
          fetch(`/api/reports/operational?year=${year}`, { headers }),
        ]);
        const [sd, pd, td, od] = await Promise.all([sr.json(), pr.json(), tr.json(), or_.json()]);
        setSummary(sd); setPayroll(pd); setTaxSched(td); setOps(od);
      } catch { /* silent */ }
      finally { setLoading(false); }
    },[]);

    useEffect(() => { fetchAll(); },[fetchAll]);

    const RPT_TABS = [
      { id:'financial', label:'Financial Summary', emoji:'💰' },
      { id:'payroll',   label:'Payroll Summary',   emoji:'👥' },
      { id:'tax',       label:'Tax Schedule',      emoji:'⚖️' },
      { id:'ops',       label:'Operational',       emoji:'📋' },
    ];

    return (
      <div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:10 }}>
          <div>
            <h2 style={{ fontFamily:"'Cinzel',serif", color:C.goldLt, fontSize:18, margin:0 }}>📊 Reports & Analytics — {year}</h2>
            <p style={{ color:C.muted, fontSize:12, margin:'4px 0 0' }}>ERUE Festival of Uzairue Kingdom — Complete Financial & Operational Reports</p>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={fetchAll} style={btnGhost}>🔄 Refresh</button>
            <button onClick={()=>window.print()} style={btnGhost}>🖨 Print</button>
          </div>
        </div>

        {/* Report type selector */}
        <div style={{ display:'flex', gap:4, marginBottom:24, borderBottom:`1px solid ${C.border}` }}>
          {RPT_TABS.map(t=>(
            <button key={t.id} onClick={()=>setActiveRpt(t.id as any)} style={{ padding:'10px 16px', background:'none', border:'none', borderBottom:`2px solid ${activeRpt===t.id?C.gold:'transparent'}`, color:activeRpt===t.id?C.goldLt:C.muted, fontSize:13, fontWeight:activeRpt===t.id?700:400, cursor:'pointer' }}>{t.emoji} {t.label}</button>
          ))}
        </div>

        {loading ? <div style={{ textAlign:'center', padding:60, color:C.muted }}>Generating reports...</div> : (
          <>
            {/* Financial Summary */}
            {activeRpt === 'financial' && summary && (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:24 }}>
                  {[
                    { label:'Total Income',      value:`₦${(summary.income||0).toLocaleString()}`,        color:C.teal },
                    { label:'Total Expenses',    value:`₦${(summary.expense||0).toLocaleString()}`,       color:C.orange },
                    { label:'Net Surplus',       value:`₦${Math.abs(summary.surplus||0).toLocaleString()} ${summary.surplus<0?'(Deficit)':''}`, color:(summary.surplus||0)>=0?'#4ade80':C.red },
                    { label:'Bank Balance',      value:`₦${(summary.bankBalance||0).toLocaleString()}`,   color:C.blue },
                    { label:'Tax Owed (WHT)',    value:`₦${(summary.taxOwed||0).toLocaleString()}`,       color:C.red },
                    { label:'Outstanding Advances', value:`₦${(summary.outstandingAdvances||0).toLocaleString()}`, color:C.orange },
                    { label:'Total Payroll',     value:`₦${(summary.totalPayroll||0).toLocaleString()}`,  color:C.purple },
                    { label:'Fixed Assets',      value:`₦${(summary.fixedAssetsValue||0).toLocaleString()}`, color:C.goldLt },
                    { label:'Inventory Value',   value:`₦${(summary.inventoryValue||0).toLocaleString()}`, color:C.teal },
                    { label:'Journal Entries',   value:summary.journals||0,                               color:C.text },
                    { label:'Budgets',           value:summary.budgets||0,                                color:C.text },
                    { label:'Account Codes',     value:summary.accounts||0,                               color:C.text },
                  ].map(k=>(
                    <div key={k.label} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px', textAlign:'center' }}>
                      <div style={{ fontSize:16, fontWeight:700, color:k.color }}>{k.value}</div>
                      <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>{k.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background:'rgba(201,168,76,0.08)', border:`1px solid ${C.gold}44`, borderRadius:10, padding:'14px 18px', fontSize:13, color:C.muted }}>
                  <strong style={{ color:C.gold }}>CAMA 2020 — Annual Filing Reminder: </strong>
                  Annual returns to CAC due by 30 June {year+1}. Audited accounts required for non-profits with income above ₦1,000,000. Maintain all receipts and payment vouchers.
                </div>
              </div>
            )}

            {/* Payroll Summary */}
            {activeRpt === 'payroll' && payroll && (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:24 }}>
                  {[
                    { label:'Total Staff', value:payroll.staff||0, color:C.blue },
                    { label:'Payroll Runs', value:payroll.runs||0, color:C.text },
                    { label:'Gross Payroll', value:`₦${(payroll.totalGross||0).toLocaleString()}`, color:C.text },
                    { label:'Net Payroll', value:`₦${(payroll.totalNet||0).toLocaleString()}`, color:'#4ade80' },
                    { label:'Total Advances', value:`₦${(payroll.totalAdvances||0).toLocaleString()}`, color:C.orange },
                    { label:'Outstanding', value:`₦${(payroll.outstandingAdvances||0).toLocaleString()}`, color:C.red },
                  ].map(k=>(
                    <div key={k.label} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px', textAlign:'center' }}>
                      <div style={{ fontSize:16, fontWeight:700, color:k.color }}>{k.value}</div>
                      <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>{k.label}</div>
                    </div>
                  ))}
                </div>
                {payroll.byType && (
                  <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
                    <div style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }}><h3 style={{ color:C.goldLt, margin:0, fontSize:14 }}>Staff by Category</h3></div>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                      <thead><tr style={{ background:C.surface }}>{['Category','Count'].map(h=><th key={h} style={{ padding:'8px 12px', textAlign:h==='Count'?'right':'left', color:C.muted, fontSize:11, borderBottom:`1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {Object.entries(payroll.byType||{}).map(([k,v])=>(
                          <tr key={k} style={{ borderBottom:`1px solid ${C.border}` }}>
                            <td style={{ padding:'8px 12px', color:C.text }}>{k}</td>
                            <td style={{ padding:'8px 12px', color:C.goldLt, textAlign:'right', fontWeight:700 }}>{String(v)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Tax Schedule */}
            {activeRpt === 'tax' && taxSched && (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:24 }}>
                  {[
                    { label:'Total Records', value:taxSched.total||0, color:C.text },
                    { label:'Total Tax', value:`₦${(taxSched.totalTax||0).toLocaleString()}`, color:C.orange },
                    { label:'Remitted', value:`₦${(taxSched.totalRemitted||0).toLocaleString()}`, color:'#4ade80' },
                  ].map(k=>(
                    <div key={k.label} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px', textAlign:'center' }}>
                      <div style={{ fontSize:16, fontWeight:700, color:k.color }}>{k.value}</div>
                      <div style={{ fontSize:11, color:C.muted }}>{k.label}</div>
                    </div>
                  ))}
                </div>
                {taxSched.byType && (
                  <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                      <thead><tr style={{ background:C.surface }}>{['Tax Type','Transactions','Gross Amount','Tax Amount','Remitted'].map(h=><th key={h} style={{ padding:'8px 12px', textAlign:['Gross Amount','Tax Amount','Remitted'].includes(h)?'right':'left', color:C.muted, fontSize:11, borderBottom:`1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {Object.entries(taxSched.byType||{}).map(([k,v]:any)=>(
                          <tr key={k} style={{ borderBottom:`1px solid ${C.border}` }}>
                            <td style={{ padding:'8px 12px', color:C.blue, fontWeight:700 }}>{k}</td>
                            <td style={{ padding:'8px 12px', color:C.text }}>{v.count}</td>
                            <td style={{ padding:'8px 12px', color:C.text, textAlign:'right' }}>₦{v.gross.toLocaleString()}</td>
                            <td style={{ padding:'8px 12px', color:C.orange, textAlign:'right', fontWeight:600 }}>₦{v.tax.toLocaleString()}</td>
                            <td style={{ padding:'8px 12px', color:'#4ade80', textAlign:'right' }}>₦{v.remitted.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Operational */}
            {activeRpt === 'ops' && ops && (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:24 }}>
                  {[
                    { label:'Total Contacts', value:ops.contacts||0, color:C.teal, emoji:'👥' },
                    { label:'Sponsors', value:ops.sponsors||0, color:C.gold, emoji:'🤝' },
                    { label:'Total Tasks', value:ops.tasks||0, color:C.blue, emoji:'✅' },
                    { label:'Overdue Tasks', value:ops.overdueTasks||0, color:C.red, emoji:'⏰' },
                    { label:'Meeting Minutes', value:ops.minutes||0, color:C.purple, emoji:'📋' },
                  ].map(k=>(
                    <div key={k.label} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px', textAlign:'center' }}>
                      <div style={{ fontSize:24 }}>{k.emoji}</div>
                      <div style={{ fontSize:20, fontWeight:700, color:k.color }}>{k.value}</div>
                      <div style={{ fontSize:11, color:C.muted }}>{k.label}</div>
                    </div>
                  ))}
                </div>
                {ops.tasksByStatus && (
                  <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
                    <div style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }}><h3 style={{ color:C.goldLt, margin:0, fontSize:14 }}>Tasks by Status</h3></div>
                    <div style={{ display:'flex', gap:12, padding:'16px', flexWrap:'wrap' }}>
                      {Object.entries(ops.tasksByStatus||{}).map(([k,v])=>{
                        const COLORS: Record<string,string> = { PENDING:C.muted, IN_PROGRESS:C.blue, BLOCKED:C.red, COMPLETED:'#4ade80', CANCELLED:C.muted };
                        return <div key={k} style={{ background:C.surface, borderRadius:10, padding:'12px 16px', textAlign:'center', minWidth:120 }}><div style={{ fontSize:18, fontWeight:700, color:COLORS[k]||C.text }}>{String(v)}</div><div style={{ fontSize:11, color:C.muted }}>{k}</div></div>;
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };


  // ── MAIN RENDER ──────────────────────────────────────────────
  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.text }}>
      {/* Tab bar */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: '0 24px', display: 'flex', gap: 2, overflowX: 'auto', flexWrap: 'nowrap' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ padding: '14px 18px', background: 'none', border: 'none', borderBottom: activeTab === tab.id ? `3px solid ${C.gold}` : '3px solid transparent', color: activeTab === tab.id ? C.goldLt : C.muted, fontSize: 13, fontWeight: activeTab === tab.id ? 700 : 400, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s' }}>
            {tab.emoji} {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '28px 24px', maxWidth: 1200, margin: '0 auto' }}>
        {activeTab === 'dashboard'     && renderDashboard()}
        {activeTab === 'accounts'      && <ChartOfAccountsPanel />}
        {activeTab === 'journals'      && <JournalLedgerPanel />}
        {activeTab === 'trial-balance' && <TrialBalancePanel />}
        {activeTab === 'ie-summary'    && <TrialBalancePanel />}
        {activeTab === 'budget'        && <BudgetPanel />}
        {activeTab === 'tasks'         && <TasksPanel />}
        {activeTab === 'minutes'       && <MinutesPanel />}
        {activeTab === 'banking'       && <BankingPanel />}
        {activeTab === 'payroll'       && <PayrollPanel />}
        {activeTab === 'tax'           && <TaxPanel />}
        {activeTab === 'inventory'     && <InventoryPanel />}
        {activeTab === 'reports'       && <ReportsPanel />}
      </div>

      {/* CAMA Footer */}
      <div style={{ textAlign: 'center', padding: '20px 24px', borderTop: `1px solid ${C.border}`, color: C.muted, fontSize: 11 }}>
        ERUE Festival of Uzairue Kingdom — Financial Records maintained under CAMA 2020 Non-Profit provisions
        © Digitrax Finteca Consulting — digitraxfinteca.com
      </div>
    </div>
  );
};

export default FinancialHub;
